/**
 * KB Backend API Service
 *
 * Provides API calls to the backend via ERPNext proxy
 * for document management, validation, knowledge base, and entity types.
 */

import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';
import { getCsrfToken } from '@/utils/csrf';

import { logger } from '@/utils/logger';
// ============================================================================
// Types
// ============================================================================

export interface BrainUser {
  user_id: string;
  username: string;
  email: string;
  role: string;
  permissions: string[];
}

// Document Types
export interface CategorySuggestion {
  category: string;
  confidence: number;
  reasoning: string;
}

export interface DocumentSummary {
  title_suggestion: string;
  summary: string;
  main_topics: string[];
  document_purpose: string;
  target_audience: string;
}

export interface DetectedEntity {
  name: string;
  type: string;
  confidence: number;
  context: string;
}

export interface MetadataSuggestions {
  suggested_categories: CategorySuggestion[];
  document_type: string;
  confidence_score: number;
  detected_language: string;
  suggested_tags: string[];
  estimated_reading_time: number;
  complexity_level: string;
  document_summary: DocumentSummary;
  detected_entities: DetectedEntity[];
}

export interface DetectedSection {
  section_title: string;
  start_page: number;
  end_page: number;
  content_preview: string;
  category_suggestion: string;
  confidence: number;
}

export interface DocumentStructure {
  total_pages: number;
  has_table_of_contents: boolean;
  detected_sections: DetectedSection[];
  processing_recommendation: string;
}

export interface DocumentAnalysisResponse {
  ingestion_id: string;
  filename: string;
  file_size: number;
  analysis_completed: boolean;
  metadata_suggestions: MetadataSuggestions;
  document_structure: DocumentStructure;
  timestamp: string;
}

export interface UploadConfirmedResponse {
  success: boolean;
  document_id: string;
  ingestion_id: string;
  message: string;
  storage_status: {
    qdrant: boolean;
    neo4j: boolean;
    postgres: boolean;
  };
  timestamp: string;
}

// Validation Types
export interface PendingValidation {
  document_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  document_type?: string;
  submitted_at: string;
  submitted_by?: string;
  complexity?: 'simple' | 'moderate' | 'complex' | 'very_complex';
  page_count?: number;
  entity_count?: number;
  chunk_count?: number;
  validation_status?: string;
  relationship_count?: number;
}

export interface PendingValidationsResult {
  documents: PendingValidation[];
  total: number;
}

export interface ValidationStats {
  total_pending: number;
  total_approved: number;
  total_rejected: number;
  approval_rate: number;
  average_validation_time_minutes: number;
  top_rejection_reasons: Array<{ reason: string; count: number }>;
}

export interface SuggestedRelationship {
  target_document_id: string;
  target_file_name: string;
  relationship_type: string;
  confidence: number;
  detection_method: string;
  reason: string;
  metadata: {
    shared_entities: Array<{
      name: string;
      type: string;
      confidence: number;
    }>;
    shared_entity_count: number;
    total_source_entities: number;
  };
  auto_approved: boolean;
}

export interface ExtractedEntity {
  entity_id: string;
  entity_type: string;
  entity_value: string;
  confidence: number;
  context?: string;
  start_pos?: number;
  end_pos?: number;
}

export interface ValidationDetail {
  document_id: string;
  tenant_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  extracted_metadata: Record<string, any>;
  suggested_tags: string[];
  extracted_entities: ExtractedEntity[];
  suggested_relationships?: SuggestedRelationship[];
  document_type?: string;
  complexity?: 'simple' | 'moderate' | 'complex' | 'very_complex';
  validation_status: string;
  submitted_by?: string;
  submitted_at: string;
  content_preview?: string;
  chunk_count?: number;
  page_count?: number;
  word_count?: number;
}

// Bulk Update Preview Types
export interface BulkUpdatePreviewResponse {
  matching_entities: number;
  affected_documents: Array<{
    document_id: string;
    file_name: string;
    matching_entities: number;
  }>;
  entity_value: string;
  from_type: string;
  to_type: string;
}

// Approved Document Types
export interface ComplianceData {
  has_compliance?: boolean;
  compliance_has_analysis?: boolean;
  compliance_primary_code?: string;
  compliance_primary_score?: number;
  compliance_primary_status?: string;
  compliance_framework_count?: number;
  compliance_overall_status?: string;
  compliance_analysis?: {
    frameworks?: Array<{
      framework_code: string;
      framework_name: string;
      overall_score: number;
      status: string;
      passing_threshold: number;
      categories_passed: number;
      categories_failed: number;
      category_results: Array<{
        category_code: string;
        category_name: string;
        score: number;
        status: string;
        threshold: number;
      }>;
      recommendations?: string[];
    }>;
    summary?: {
      has_compliance?: boolean;
      primary_badge?: {
        code: string;
        score: number;
        status: string;
      };
    };
  };
}

export interface ApprovedDocument {
  document_id: string;
  hash_id?: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_attachment?: string;
  document_type: string;
  approved_at: string;
  entity_count: number;
  knowledge_base?: string | null;
  kb_document_name?: string | null;
  kb_file_path?: string;
  metadata?: ComplianceData;
  // Where the file came from: "Manual upload" for device uploads, or the
  // cloud provider name ("Google Drive" / "SharePoint") when synced from a
  // KB Cloud Folder Source.
  source?: string;
  cloud_source?: string | null;
}

export interface ApprovedDocumentsResponse {
  data: ApprovedDocument[];
  total: number;
  with_knowledge_base: number;
  without_knowledge_base: number;
  /** SUM(file_size) over the filtered set (bytes); absent on older backends */
  total_size?: number;
  limit: number;
  offset: number;
}

export interface ApprovedDocumentDetail {
  document_id: string;
  tenant_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  extracted_metadata: Record<string, any>;
  suggested_tags: string[];
  extracted_entities: Array<{
    entity_id: string;
    entity_type: string;
    entity_value: string;
    confidence: number;
    context?: string;
    manually_corrected?: boolean;
    previous_type?: string;
  }>;
  validation_status: string;
  submitted_at: string;
  approved_at: string;
  approval_notes?: string;
}

// Entity Relationship Types
export interface EntityRelationshipEntity {
  id: string;
  value: string;
  type: string;
  confidence?: number;
}

export interface EntityRelationshipInfo {
  type: string;
  confidence: number;
  context?: string;
}

export interface EntityRelationshipDetail {
  source: EntityRelationshipEntity;
  relationship: EntityRelationshipInfo;
  target: EntityRelationshipEntity;
}

export interface EntityRelationshipsResponse {
  document_id: string;
  total_relationships: number;
  relationships: EntityRelationshipDetail[];
}

// Entity Type Types
export interface EntityType {
  id: number;
  name: string;
  code: string;
  label: string;
  description?: string;
  category: string;
  parent_type?: string;
  importance_weight: number;
  patterns: string[];
  vocabulary: string[];
  is_active: boolean;
  is_system: boolean;
  // When true, the type applies to every Domain Knowledge Template
  // via the universal union and does NOT require per-industry
  // whitelist entries. Server-side default: false.
  is_universal?: boolean;
  // Synonym-collapse: when true, this type is an ALIAS of parent_type
  // (the master). Future extractions are redirected to the master and
  // this type is deactivated. Server default: false.
  collapse_to_parent?: boolean;
  // True when the type was auto-discovered (LLM-invented) rather than
  // curated. Used to keep noise out of the Parent Type picker.
  auto_discovered?: boolean;
  // Domain Knowledge Template name this type was first created
  // from. Read-only / informational; server auto-populates on
  // insert from the active industry or an explicit target.
  origin_template?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface EntityTypeSummary {
  id: number;
  name: string;
  code: string;
  label: string;
  category: string;
  importance_weight: number;
  is_active: boolean;
  is_system: boolean;
  vocabulary_count: number;
  pattern_count: number;
}

export interface EntityTypeCategory {
  category: string;
  label: string;
  description: string;
  count: number;
}

export interface EntityTypeCreate {
  name: string;
  code?: string;
  label: string;
  description?: string;
  category: string;
  parent_type?: string;
  importance_weight?: number;
  patterns?: string[];
  vocabulary?: string[];
  is_active?: boolean;
  // When true the new type is flagged universal and skips the
  // per-industry whitelist link. Defaults to false on the server.
  is_universal?: boolean;
  // Optional explicit Domain Knowledge Template the type should be
  // linked to. Defaults to Brain Settings.domain_knowledge_template
  // server-side. Set to override the active industry without first
  // switching Brain Settings.
  target_template?: string;
}

export interface EntityTypeUpdate {
  name?: string;
  code?: string;
  label?: string;
  description?: string;
  category?: string;
  parent_type?: string;
  importance_weight?: number;
  patterns?: string[];
  vocabulary?: string[];
  is_active?: boolean;
  is_universal?: boolean;
  // Enable synonym-collapse: fold this type into parent_type (the
  // master). Requires parent_type to be set. Triggers a one-time
  // backfill server-side and deactivates this type.
  collapse_to_parent?: boolean;
}

// ============================================================================
// Authentication Service
// ============================================================================

export const brainAuthService = {
  /**
   * Login via ERPNext.
   * Uses ERP endpoint: /api/method/login
   */
  async login(username: string, password: string): Promise<{ message: string; full_name?: string }> {
    const endpoint = getApiEndpoint('LOGIN');
    if (!endpoint) {
      throw new Error('LOGIN endpoint not configured');
    }

    const response = await axios.post<{ message: string; full_name?: string }>(
      endpoint,
      { usr: username, pwd: password },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  },

  /**
   * Get current user info via ERPNext.
   * Uses ERP endpoint: orgain.user_api.get_user_details
   */
  async getCurrentUser(): Promise<BrainUser> {
    const endpoint = getApiEndpoint('GET_CURRENT_USER');
    if (!endpoint) {
      throw new Error('GET_CURRENT_USER endpoint not configured');
    }

    const response = await axios.post<{ message: { data: BrainUser } }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message.data;
  },

  /**
   * Logout via ERPNext.
   * Uses ERP endpoint: /api/method/logout
   */
  async logout(): Promise<void> {
    const endpoint = getApiEndpoint('LOGOUT');
    if (endpoint) {
      try {
        await axios.post(
          endpoint,
          {},
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
          }
        );
      } catch (error) {
        logger.warn('Logout API call failed:', error);
      }
    }
  },
};

// ============================================================================
// Document Upload Service
// ============================================================================

export const documentService = {
  /**
   * Report whether the LLM API keys the ingestion pipeline needs (entity
   * extraction, embeddings) are configured. The upload page gates on this so
   * the user gets a clear "configure your key" message instead of a silent
   * server-side rejection deep in processing.
   * Uses ERP endpoint: orgain.kb.api.document_api.check_llm_configuration
   */
  async checkLlmConfiguration(): Promise<{
    ok: boolean;
    message?: string;
    providers_missing: string[];
    missing: Array<{ role: string; provider: string }>;
    checked: Array<{ role: string; provider: string; model: string; configured: boolean }>;
  }> {
    const endpoint = getApiEndpoint('CHECK_LLM_CONFIGURATION');
    if (!endpoint) {
      throw new Error('CHECK_LLM_CONFIGURATION endpoint not configured');
    }
    const response = await axios.get<{ message: {
      ok: boolean;
      message?: string;
      providers_missing: string[];
      missing: Array<{ role: string; provider: string }>;
      checked: Array<{ role: string; provider: string; model: string; configured: boolean }>;
    } }>(endpoint, { withCredentials: true });
    return response.data.message;
  },

  /**
   * Bulk delete unapproved documents.
   * Uses ERP endpoint: orgain.kb.api.document_api.bulk_delete_unapproved_documents
   */
  async bulkDeleteUnapproved(documentIds: string[]): Promise<{
    status: string;
    job_id: string;
    total_documents: number;
    document_ids: string[];
    message: string;
  }> {
    const endpoint = getApiEndpoint('BULK_DELETE_UNAPPROVED_DOCUMENTS');
    if (!endpoint) {
      throw new Error('BULK_DELETE_UNAPPROVED_DOCUMENTS endpoint not configured');
    }

    const response = await axios.post<{ message: {
      status: string;
      job_id: string;
      total_documents: number;
      document_ids: string[];
      message: string;
    } }>(
      endpoint,
      { document_ids: documentIds },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Check status of a bulk delete background job.
   * Uses ERP endpoint: orgain.kb.api.document_api.get_unapproved_bulk_delete_status
   */
  async getBulkDeleteStatus(jobId: string): Promise<{
    job_id: string;
    status: string;
    started_at: string | null;
    ended_at: string | null;
    error: string | null;
  }> {
    const endpoint = getApiEndpoint('BULK_DELETE_STATUS');
    if (!endpoint) {
      throw new Error('BULK_DELETE_STATUS endpoint not configured');
    }

    const response = await axios.post<{ message: any }>(
      endpoint,
      { job_id: jobId },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Pre-validate files before upload. Fast check: file type, size, duplicate hash.
   * Uses ERP endpoint: orgain.kb.api.document_api.pre_validate_documents
   *
   * Metadata only - the file bytes are NOT sent. The text-content duplicate
   * check runs server-side at upload instead; shipping every file here just to
   * pre-empt it meant uploading each one twice and timed the request out.
   */
  async preValidateDocuments(filesInfo: Array<{
    filename: string;
    file_size: number;
    content_hash?: string;
  }>): Promise<{
    status: string;
    total: number;
    valid_count: number;
    invalid_count: number;
    results: Array<{
      filename: string;
      valid: boolean;
      errors: string[];
      duplicate?: { document_id: string; title: string; status: string } | null;
    }>;
    max_file_size: number;
    supported_extensions: string[];
  }> {
    const endpoint = getApiEndpoint('PRE_VALIDATE_DOCUMENTS');
    if (!endpoint) {
      throw new Error('PRE_VALIDATE_DOCUMENTS endpoint not configured');
    }

    const response = await axios.post<{ message: {
      status: string;
      total: number;
      valid_count: number;
      invalid_count: number;
      results: Array<{
        filename: string;
        valid: boolean;
        errors: string[];
        duplicate?: { document_id: string; title: string; status: string } | null;
      }>;
      max_file_size: number;
      supported_extensions: string[];
    } }>(
      endpoint,
      { files_info: JSON.stringify(filesInfo) },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Batch upload pre-uploaded files: create Documents and queue background processing.
   * Uses ERP endpoint: orgain.kb.api.document_api.batch_upload_documents
   */
  async batchUploadDocuments(documents: Array<{
    file_url: string;
    title?: string;
    category?: string;
    document_type?: string;
    department?: string;
    access_level?: string;
    parent_folder?: string;
  }>): Promise<{
    status: string;
    total: number;
    queued_count: number;
    failed_count: number;
    results: Array<{
      filename: string;
      document_id?: string;
      title?: string;
      status: string;
      job_id?: string;
      knowledge_base?: string;
      message?: string;
    }>;
    message: string;
  }> {
    const endpoint = getApiEndpoint('BATCH_UPLOAD_DOCUMENTS');
    if (!endpoint) {
      throw new Error('BATCH_UPLOAD_DOCUMENTS endpoint not configured');
    }

    const response = await axios.post<{ message: {
      status: string;
      total: number;
      queued_count: number;
      failed_count: number;
      results: Array<{
        filename: string;
        document_id?: string;
        title?: string;
        status: string;
        job_id?: string;
        knowledge_base?: string;
        message?: string;
      }>;
      message: string;
    } }>(
      endpoint,
      { documents: JSON.stringify(documents) },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        timeout: 300000, // 5 minutes for batch processing
      }
    );

    return response.data.message;
  },

  /**
   * Save a chat response to the Knowledge Base.
   * Creates a markdown document and processes it through the ingestion pipeline.
   */
  async saveResponseToKB(
    content: string,
    title: string,
    documentType?: string,
    category?: string
  ): Promise<{ status: string; message: string; document_name?: string }> {
    const endpoint = getApiEndpoint('SAVE_CHAT_RESPONSE_TO_KB');
    if (!endpoint) {
      throw new Error('SAVE_CHAT_RESPONSE_TO_KB endpoint not configured');
    }

    const response = await axios.post<{ message: { status: string; message: string; document_name?: string } }>(
      endpoint,
      {
        content,
        title,
        document_type: documentType,
        category,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        timeout: 60000,
      }
    );

    return response.data.message;
  },

  /**
   * Check if a document with the given title already exists in the Knowledge Base.
   */
  async checkTitleExists(title: string): Promise<boolean> {
    const endpoint = getApiEndpoint('CHECK_DOCUMENT_TITLE_EXISTS');
    if (!endpoint) return false;

    const response = await axios.post<{ message: { exists: boolean } }>(
      endpoint,
      { title },
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
    );

    return response.data.message.exists;
  },

  /**
   * Submit thumbs up/down feedback for a chat response.
   */
  async submitChatFeedback(params: {
    session_id: string;
    message_index?: number;
    feedback_type: 'thumbs_up' | 'thumbs_down';
    query?: string;
    response_preview?: string;
  }): Promise<{ status: string; message: string }> {
    const endpoint = getApiEndpoint('SUBMIT_CHAT_FEEDBACK');
    if (!endpoint) throw new Error('SUBMIT_CHAT_FEEDBACK endpoint not configured');

    const response = await axios.post<{ message: { status: string; message: string } }>(
      endpoint,
      params,
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
    );
    return response.data.message;
  },
};

// ============================================================================
// Validation Service
// ============================================================================

export const validationService = {
  /**
   * Get documents pending validation with pagination.
   * Uses ERP endpoint: orgain.kb.api.validation_api.get_pending_validations
   */
  async getPendingValidations(params?: {
    page?: number;
    page_size?: number;
    priority?: string;
    document_type?: string;
    search?: string;
    file_type?: string;
    min_file_size?: string;
    max_file_size?: string;
    submitted_after?: string;
    submitted_till?: string;
    // Legacy params (kept for backward compatibility, mapped to new params)
    limit?: number;
    offset?: number;
  }): Promise<PendingValidationsResult> {
    const endpoint = getApiEndpoint('GET_PENDING_VALIDATIONS');
    if (!endpoint) {
      throw new Error('GET_PENDING_VALIDATIONS endpoint not configured');
    }

    // Map legacy params to new ERP params
    const page = params?.page ?? (params?.offset ? Math.floor(params.offset / (params?.limit ?? 20)) + 1 : 1);
    const page_size = params?.page_size ?? params?.limit ?? 20;

    const payload: Record<string, any> = {
      page,
      page_size,
      priority: params?.priority,
    };
    if (params?.document_type) payload.document_type = params.document_type;
    if (params?.search) payload.search = params.search;
    if (params?.file_type) payload.file_type = params.file_type;
    if (params?.min_file_size) payload.min_file_size = params.min_file_size;
    if (params?.max_file_size) payload.max_file_size = params.max_file_size;
    if (params?.submitted_after) payload.submitted_after = params.submitted_after;
    if (params?.submitted_till) payload.submitted_till = params.submitted_till;

    const response = await axios.post<{ message: any }>(
      endpoint,
      payload,
      {
        withCredentials: true,
        // Search-path request: fail visibly rather than spin forever.
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const message = response.data.message;

    // Check for ERP error response format
    if (message?.status === 'error') {
      throw new Error(message?.message || 'Failed to fetch pending validations');
    }

    // ERP responses wrap data in 'message.data' or 'message.documents' property
    const data = message?.data ?? message;
    let docs: any[];
    let total: number = 0;
    if (Array.isArray(data)) {
      docs = data;
      total = data.length;
    } else if (data?.documents && Array.isArray(data.documents)) {
      docs = data.documents;
      total = data.pagination?.total ?? data.total ?? docs.length;
    } else {
      return { documents: [], total: 0 };
    }

    // Map ERP field names to frontend PendingValidation interface
    const documents = docs.map((doc: any) => ({
      document_id: doc.document_id || doc.name,
      file_name: doc.file_name,
      file_type: doc.file_type,
      file_size: doc.file_size,
      document_type: doc.document_type,
      submitted_at: doc.submitted_at || doc.processing_completed_at,
      submitted_by: doc.submitted_by,
      complexity: doc.complexity || (doc.complexity_level ? doc.complexity_level.toLowerCase() : undefined),
      page_count: doc.page_count,
      entity_count: doc.entity_count,
      chunk_count: doc.chunk_count,
      validation_status: doc.validation_status,
      relationship_count: doc.relationship_count,
    })) as PendingValidation[];

    return { documents, total };
  },

  /**
   * Get validation statistics for a specified period.
   * Uses ERP endpoint: orgain.kb.api.validation_api.get_validation_stats
   */
  async getValidationStats(days: number = 7): Promise<ValidationStats> {
    const endpoint = getApiEndpoint('GET_VALIDATION_STATS');
    if (!endpoint) {
      throw new Error('GET_VALIDATION_STATS endpoint not configured');
    }

    const response = await axios.post<{ message: ValidationStats }>(
      endpoint,
      { days },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Get detailed information about a document pending validation.
   * Uses ERP endpoint: orgain.kb.api.validation_api.get_validation_details
   */
  async getValidationDetail(documentId: string, forceRefresh: boolean = false): Promise<ValidationDetail> {
    const endpoint = getApiEndpoint('GET_VALIDATION_DETAILS');
    if (!endpoint) {
      throw new Error('GET_VALIDATION_DETAILS endpoint not configured');
    }

    const response = await axios.post<{ message: any }>(
      endpoint,
      {
        document_name: documentId,
        force_refresh: forceRefresh,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const message = response.data.message;

    // Check for ERP error response format
    if (message?.status === 'error') {
      throw new Error(message?.message || 'Failed to fetch validation details');
    }

    // Handle both direct data and nested data property
    const data = message?.data || message;

    // Validate that we have the required fields for ValidationDetail
    if (!data || (!data.document_id && !data.name)) {
      throw new Error('Invalid response: missing document data');
    }

    // Map ERP field names to frontend ValidationDetail interface
    if (!data.document_id && data.name) {
      data.document_id = data.name;
    }
    if (!data.submitted_at && data.processing_completed_at) {
      data.submitted_at = data.processing_completed_at;
    }
    if (!data.complexity && data.complexity_level) {
      data.complexity = data.complexity_level.toLowerCase();
    }

    return data;
  },

  /**
   * Approve document and move from staging to production.
   * Uses ERP endpoint: orgain.kb.api.validation_api.approve_document
   */
  async approveDocument(
    documentId: string,
    approvalNotes?: string,
    correctedMetadata?: Record<string, any>,
    options?: {
      skip_low_confidence?: boolean;
      confidence_threshold?: number;
    }
  ): Promise<{ status: string; production_document_id: string }> {
    const endpoint = getApiEndpoint('APPROVE_DOCUMENT');
    if (!endpoint) {
      throw new Error('APPROVE_DOCUMENT endpoint not configured');
    }

    const response = await axios.post<{ message: { status: string; production_document_id: string } }>(
      endpoint,
      {
        document_name: documentId,
        approval_notes: approvalNotes,
        corrected_metadata: correctedMetadata ? JSON.stringify(correctedMetadata) : undefined,
        skip_low_confidence: options?.skip_low_confidence ?? false,
        confidence_threshold: options?.confidence_threshold ?? 0.7,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Reject document and remove from staging.
   * Uses ERP endpoint: orgain.kb.api.validation_api.reject_document
   */
  async rejectDocument(
    documentId: string,
    rejectionReason: string,
    rejectionNotes?: string,
    deleteFromStaging: boolean = true
  ): Promise<{ status: string }> {
    const endpoint = getApiEndpoint('REJECT_DOCUMENT');
    if (!endpoint) {
      throw new Error('REJECT_DOCUMENT endpoint not configured');
    }

    const response = await axios.post<{ message: { status: string } }>(
      endpoint,
      {
        document_name: documentId,
        rejection_reason: rejectionReason,
        rejection_notes: rejectionNotes,
        delete_from_staging: deleteFromStaging,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Approve multiple documents at once.
   * Uses ERP endpoint: orgain.kb.api.validation_api.bulk_approve_documents
   */
  async bulkApprove(
    documentIds: string[],
    approvalNotes?: string
  ): Promise<{
    approved_count: number;
    failed_count: number;
    failures: Array<{ document_id: string; error: string }>;
  }> {
    const endpoint = getApiEndpoint('BULK_APPROVE_DOCUMENTS');
    if (!endpoint) {
      throw new Error('BULK_APPROVE_DOCUMENTS endpoint not configured');
    }

    const response = await axios.post<{ message: {
      approved_count: number;
      failed_count: number;
      failures: Array<{ document_id: string; error: string }>;
    } }>(
      endpoint,
      {
        document_ids: JSON.stringify(documentIds),
        approval_notes: approvalNotes,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Get a quick summary of the validation queue.
   * Uses ERP endpoint: orgain.kb.api.validation_api.get_queue_summary
   */
  async getQueueSummary(): Promise<QueueSummary> {
    const endpoint = getApiEndpoint('GET_QUEUE_SUMMARY');
    if (!endpoint) {
      throw new Error('GET_QUEUE_SUMMARY endpoint not configured');
    }

    const response = await axios.post<{ message: QueueSummary }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Update document metadata while in staging.
   * Uses ERP endpoint: orgain.kb.api.validation_api.update_document_metadata
   */
  async updateMetadata(
    documentId: string,
    data: {
      title?: string;
      author?: string;
      tags?: string[];
      custom_metadata?: Record<string, any>;
      document_type?: string;
      entity_corrections?: Array<{
        entity_id: string;
        corrected_value?: string;
        corrected_type?: string;
        action: 'accept' | 'reject' | 'modify';
      }>;
    }
  ): Promise<{ status: string; document_id: string; message: string }> {
    const endpoint = getApiEndpoint('UPDATE_DOCUMENT_METADATA');
    if (!endpoint) {
      throw new Error('UPDATE_DOCUMENT_METADATA endpoint not configured');
    }

    const response = await axios.post<{ message: { status: string; document_id: string; message: string } }>(
      endpoint,
      {
        document_name: documentId,
        title: data.title,
        author: data.author,
        tags: data.tags ? JSON.stringify(data.tags) : undefined,
        document_type: data.document_type,
        custom_metadata: data.custom_metadata ? JSON.stringify(data.custom_metadata) : undefined,
        entity_corrections: data.entity_corrections ? JSON.stringify(data.entity_corrections) : undefined,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },
};

// ============================================================================
// Approved Documents (Knowledge Base) Service
// ============================================================================

export const knowledgeService = {
  /**
   * Get active document types for filter dropdowns.
   * Uses ERP endpoint: orgain.kb.api.document_api.get_active_document_types
   * Requires: Login
   */
  async getActiveDocumentTypes(): Promise<Array<{
    name: string; type_name: string; description: string; file_extensions: string;
  }>> {
    const endpoint = getApiEndpoint('GET_ACTIVE_DOCUMENT_TYPES');
    if (!endpoint) {
      throw new Error('GET_ACTIVE_DOCUMENT_TYPES endpoint not configured');
    }

    const response = await axios.post<{ message: any }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return Array.isArray(response.data.message) ? response.data.message : [];
  },

  /**
   * List approved documents.
   * Uses ERP endpoint: orgain.kb.api.approved_documents_api.list_approved_documents
   * Requires: Login (owner or admin)
   */
  async getApprovedDocuments(params?: {
    limit?: number;
    offset?: number;
    search?: string;
    file_type?: string;
    document_type?: string;
    knowledge_base_categorized?: boolean;
    approved_from?: string;
    approved_till?: string;
    // 'manual' | 'google' | 'sharepoint' - narrows by where the file came from
    source?: string;
  }): Promise<ApprovedDocumentsResponse> {
    const endpoint = getApiEndpoint('LIST_APPROVED_DOCUMENTS');
    if (!endpoint) {
      throw new Error('LIST_APPROVED_DOCUMENTS endpoint not configured');
    }

    // Build payload with all filter params
    const payload: Record<string, any> = {
      limit: params?.limit ?? 50,
      offset: params?.offset ?? 0,
    };

    // Add optional filter params only if they have values
    if (params?.search) payload.search = params.search;
    if (params?.file_type) payload.file_type = params.file_type;
    if (params?.document_type) payload.document_type = params.document_type;
    if (params?.knowledge_base_categorized !== undefined) payload.knowledge_base_categorized = params.knowledge_base_categorized;
    if (params?.approved_from) payload.approved_from = params.approved_from;
    if (params?.approved_till) payload.approved_till = params.approved_till;
    if (params?.source) payload.source = params.source;

    const response = await axios.post<{ message: ApprovedDocumentsResponse | ApprovedDocument[] }>(
      endpoint,
      payload,
      {
        withCredentials: true,
        // Without a timeout a stalled request leaves the library spinning
        // forever. 30s tolerates a cold gunicorn worker on a large library
        // while still failing visibly rather than hanging.
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const message = response.data.message;

    // Handle both response formats: direct array (legacy) or full response with stats
    if (Array.isArray(message)) {
      // Legacy format - wrap in response structure
      return {
        data: message,
        total: message.length,
        with_knowledge_base: 0,
        without_knowledge_base: 0,
        limit: params?.limit ?? 50,
        offset: params?.offset ?? 0,
      };
    }

    return message;
  },

  /**
   * List approved spreadsheets (csv/xlsx/xls) for the chat "Pick a spreadsheet"
   * control. Unlike getApprovedDocuments (admin-only, one call PER file type),
   * this is callable by any authenticated user and returns every approved
   * spreadsheet in ONE request.
   */
  async listChatSpreadsheets(): Promise<ApprovedDocument[]> {
    const endpoint = getApiEndpoint('LIST_CHAT_SPREADSHEETS');
    if (!endpoint) throw new Error('LIST_CHAT_SPREADSHEETS endpoint not configured');
    const response = await axios.post<{ message: ApprovedDocument[] }>(
      endpoint,
      {},
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    );
    return Array.isArray(response.data.message) ? response.data.message : [];
  },

  /**
   * Get approved document by id.
   * Uses ERP endpoint: orgain.kb.api.approved_documents_api.get_approved_document_detail
   */
  async getDocumentDetail(documentId: string): Promise<ApprovedDocumentDetail> {
    const endpoint = getApiEndpoint('GET_APPROVED_DOCUMENT_DETAIL');
    if (!endpoint) {
      throw new Error('GET_APPROVED_DOCUMENT_DETAIL endpoint not configured');
    }

    const response = await axios.post<{ message: any }>(
      endpoint,
      {
        document_id: documentId,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const message = response.data.message;

    // Check for ERP error response format
    if (message?.status === 'error') {
      throw new Error(message?.message || 'Failed to fetch document details');
    }

    // Handle both direct data and nested data property
    const data = message?.data || message;

    // Validate that we have the required fields
    if (!data || !data.document_id) {
      throw new Error('Invalid response: missing document data');
    }

    return data;
  },

  /**
   * Get entity-to-entity semantic relationships for a document.
   * Returns relationships like WORKS_AT, MANAGES, USES, LOCATED_AT, etc.
   */
  async getEntityRelationships(
    documentId: string,
    minConfidence: number = 0.0
  ): Promise<EntityRelationshipsResponse> {
    const endpoint = getApiEndpoint('GET_ENTITY_RELATIONSHIPS');
    if (!endpoint) {
      throw new Error('GET_ENTITY_RELATIONSHIPS endpoint not configured');
    }

    const response = await axios.post<{ message: EntityRelationshipsResponse }>(
      endpoint,
      {
        document_id: documentId,
        min_confidence: minConfidence,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const message = response.data.message;

    // Check for ERP error response format
    if ((message as any)?.status === 'error') {
      throw new Error((message as any)?.message || 'Failed to fetch entity relationships');
    }

    return message;
  },

  /**
   * Delete approved document by id.
   * Uses ERP endpoint: orgain.kb.api.approved_documents_api.delete_approved_document
   */
  async deleteDocument(documentId: string): Promise<{
    status: string;
    cleanup: { postgresql: boolean; qdrant: boolean; neo4j: boolean };
  }> {
    const endpoint = getApiEndpoint('DELETE_APPROVED_DOCUMENT');
    if (!endpoint) {
      throw new Error('DELETE_APPROVED_DOCUMENT endpoint not configured');
    }

    const response = await axios.post<{ message: {
      status: string;
      cleanup: { postgresql: boolean; qdrant: boolean; neo4j: boolean };
    } }>(
      endpoint,
      {
        document_id: documentId,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Get download url for approved document.
   * Uses ERP endpoint: orgain.kb.api.approved_documents_api.download_approved_document
   */
  async getDownloadUrl(documentId: string): Promise<{
    download_url: string;
    expires_in_seconds: number;
    file_name: string;
  }> {
    const endpoint = getApiEndpoint('DOWNLOAD_APPROVED_DOCUMENT');
    if (!endpoint) {
      throw new Error('DOWNLOAD_APPROVED_DOCUMENT endpoint not configured');
    }

    const response = await axios.post<{ message: {
      download_url: string;
      expires_in_seconds: number;
      file_name: string;
    } }>(
      endpoint,
      {
        document_id: documentId,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Update entity type for an approved document.
   * Uses ERP endpoint: orgain.kb.api.approved_documents_api.update_entity_type
   * Requires: Login (owner or admin)
   */
  async updateEntityType(
    documentId: string,
    entityId: string | number,
    newType: string
  ): Promise<{ status: string }> {
    const endpoint = getApiEndpoint('UPDATE_DOCUMENT_ENTITY_TYPE');
    if (!endpoint) {
      throw new Error('UPDATE_DOCUMENT_ENTITY_TYPE endpoint not configured');
    }

    const response = await axios.post<{ message: { status: string } }>(
      endpoint,
      {
        document_id: documentId,
        entity_id: entityId,
        new_type: newType,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Get unique entity values across all approved documents.
   * Uses ERP endpoint: orgain.kb.api.approved_documents_api.get_unique_entity_values
   * Requires: Login (owner or admin)
   *
   * @param entityType - The entity type to filter by (required)
   */
  async getUniqueEntityValues(entityType: string): Promise<
    Array<{ value: string; type: string; count: number }>
  > {
    const endpoint = getApiEndpoint('GET_UNIQUE_ENTITY_VALUES');
    if (!endpoint) {
      throw new Error('GET_UNIQUE_ENTITY_VALUES endpoint not configured');
    }

    const response = await axios.post<{ message: Array<{ value: string; type: string; count: number }> | { data: Array<{ value: string; type: string; count: number }> } }>(
      endpoint,
      {
        entity_type: entityType,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Handle both response formats: direct array or wrapped in data
    const message = response.data.message;
    return Array.isArray(message) ? message : message.data;
  },

  /**
   * Bulk update entity types across multiple approved documents.
   * Uses ERP endpoint: orgain.kb.api.approved_documents_api.bulk_update_entity_types
   * Requires: Login (owner or admin)
   */
  async bulkUpdateEntityTypes(params: {
    entity_value: string;
    from_type: string;
    to_type: string;
    document_ids?: string[];
  }): Promise<{
    success: boolean;
    documents_updated: number;
    entities_updated: number;
  }> {
    const endpoint = getApiEndpoint('BULK_UPDATE_ENTITY_TYPES');
    if (!endpoint) {
      throw new Error('BULK_UPDATE_ENTITY_TYPES endpoint not configured');
    }

    const response = await axios.post<{ message: {
      success: boolean;
      documents_updated: number;
      entities_updated: number;
    } }>(
      endpoint,
      {
        entity_value: params.entity_value,
        from_type: params.from_type,
        to_type: params.to_type,
        document_ids: params.document_ids ? JSON.stringify(params.document_ids) : undefined,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Preview what would be affected by a bulk entity type update.
   * Uses ERP endpoint: orgain.kb.api.approved_documents_api.preview_bulk_entity_update
   * Requires: Login (owner or admin)
   */
  async previewBulkUpdate(params: {
    entity_value: string;
    from_type: string;
    to_type: string;
    document_ids?: string[];
  }): Promise<BulkUpdatePreviewResponse> {
    const endpoint = getApiEndpoint('PREVIEW_BULK_ENTITY_UPDATE');
    if (!endpoint) {
      throw new Error('PREVIEW_BULK_ENTITY_UPDATE endpoint not configured');
    }

    const response = await axios.post<{ message: BulkUpdatePreviewResponse }>(
      endpoint,
      {
        entity_value: params.entity_value,
        from_type: params.from_type,
        to_type: params.to_type,
        document_ids: params.document_ids ? JSON.stringify(params.document_ids) : undefined,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Update entity value for an approved document.
   * Uses ERP endpoint: orgain.kb.api.approved_documents_api.update_entity_value
   */
  async updateEntityValue(
    documentId: string,
    entityId: string,
    newValue: string
  ): Promise<{ status: string }> {
    const endpoint = getApiEndpoint('KB_UPDATE_ENTITY_VALUE');
    if (!endpoint) {
      throw new Error('KB_UPDATE_ENTITY_VALUE endpoint not configured');
    }

    const response = await axios.post<{ message: { status: string } }>(
      endpoint,
      {
        document_id: documentId,
        entity_id: entityId,
        new_value: newValue,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Bulk link documents to a knowledge base folder.
   * Uses ERP endpoint: orgain.kb.api.document_api.bulk_link_documents_to_knowledge_base
   */
  async bulkLinkDocumentsToKB(documentIds: string[], kbFolderId: string): Promise<{
    status: string;
    linked: number;
    failed: number;
  }> {
    const endpoint = getApiEndpoint('BULK_LINK_DOCUMENTS_TO_KB');
    if (!endpoint) {
      throw new Error('BULK_LINK_DOCUMENTS_TO_KB endpoint not configured');
    }

    const response = await axios.post<{ message: { status: string; linked: number; failed: number } }>(
      endpoint,
      {
        document_ids: documentIds,
        parent_folder: kbFolderId,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Bulk unlink documents from their knowledge base.
   * Uses ERP endpoint: orgain.kb.api.document_api.bulk_unlink_documents_from_knowledge_base
   */
  async bulkUnlinkDocumentsFromKB(documentIds: string[]): Promise<{
    status: string;
    unlinked: number;
    failed: number;
  }> {
    const endpoint = getApiEndpoint('BULK_UNLINK_DOCUMENTS_FROM_KB');
    if (!endpoint) {
      throw new Error('BULK_UNLINK_DOCUMENTS_FROM_KB endpoint not configured');
    }

    const response = await axios.post<{ message: { status: string; unlinked: number; failed: number } }>(
      endpoint,
      {
        document_ids: documentIds,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },
};

// ============================================================================
// Entity Type Service
// ============================================================================

export const entityTypeService = {
  /**
   * Get all entity types with pagination, search, and filtering.
   * Uses ERP endpoint: orgain.kb.api.entity_type_api.get_entity_types
   * Requires: Login
   *
   * @param params.skip - Number of records to skip (default: 0)
   * @param params.limit - Maximum records, max 1000 (default: 100)
   * @param params.search - Search by name or label
   * @param params.category - Filter by category
   * @param params.active_only - Only show active types (default: true)
   * @param params.include_system - Include system types (default: true)
   */
  async getEntityTypes(params?: {
    skip?: number;
    limit?: number;
    search?: string;
    category?: string;
    active_only?: boolean;
    include_system?: boolean;
    // When omitted, the backend default applies (scopes the result to
    // the active Domain Knowledge Template's whitelist + universals).
    // Pass `false` to fetch the full, unscoped catalogue - required by
    // the admin governance page so in-use types that aren't whitelisted
    // by the active template (e.g. SERVICE, SERVICE_TYPE) still show.
    scope_to_active_template?: boolean;
  }): Promise<EntityType[]> {
    const endpoint = getApiEndpoint('GET_ENTITY_TYPES');
    if (!endpoint) {
      throw new Error('GET_ENTITY_TYPES endpoint not configured');
    }

    const response = await axios.post<{ message: EntityType[] | { data: EntityType[] } }>(
      endpoint,
      {
        skip: params?.skip ?? 0,
        limit: params?.limit ?? 100,
        search: params?.search,
        category: params?.category,
        active_only: params?.active_only !== false ? 'true' : 'false',
        include_system: params?.include_system !== false ? 'true' : 'false',
        // Only send the key when the caller is explicit so other callers
        // (dropdown option hooks) keep the scoped backend default.
        scope_to_active_template:
          params?.scope_to_active_template === undefined
            ? undefined
            : params.scope_to_active_template
              ? 'true'
              : 'false',
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Handle both response formats: direct array or wrapped in data
    const message = response.data.message;
    return Array.isArray(message) ? message : message.data;
  },

  /**
   * Server-side paginated entity types for the admin governance list.
   * Uses ERP endpoint: orgain.kb.api.entity_type_api.get_entity_types_page
   * Returns the current page plus the total row count for the active
   * filter set, so the table can render real pagination.
   */
  async getEntityTypesPage(params: {
    skip?: number;
    limit?: number;
    search?: string;
    category?: string;
    status?: 'active' | 'inactive' | 'all';
    include_system?: boolean;
    scope_to_active_template?: boolean;
    order_by?: string;
    has_vocabulary?: 'all' | 'with' | 'without';
  }): Promise<{ data: EntityType[]; total: number; skip: number; limit: number }> {
    const endpoint = getApiEndpoint('GET_ENTITY_TYPES_PAGE');
    if (!endpoint) {
      throw new Error('GET_ENTITY_TYPES_PAGE endpoint not configured');
    }

    const response = await axios.post<{ message: { data: EntityType[]; total: number; skip: number; limit: number } }>(
      endpoint,
      {
        skip: params.skip ?? 0,
        limit: params.limit ?? 25,
        search: params.search || undefined,
        category: params.category || undefined,
        status: params.status ?? 'all',
        include_system: params.include_system !== false ? 'true' : 'false',
        scope_to_active_template: params.scope_to_active_template ? 'true' : 'false',
        order_by: params.order_by || undefined,
        has_vocabulary: params.has_vocabulary ?? 'all',
      },
      {
        withCredentials: true,
        // Search-path request: fail visibly rather than spin forever.
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Get a summary list of entity types (for dropdowns).
   * Uses ERP endpoint: orgain.kb.api.entity_type_api.get_entity_types_summary
   * Requires: Login
   *
   * @param activeOnly - Only show active types (default: true)
   */
  async getEntityTypeSummary(activeOnly: boolean = true): Promise<EntityTypeSummary[]> {
    const endpoint = getApiEndpoint('GET_ENTITY_TYPES_SUMMARY');
    if (!endpoint) {
      throw new Error('GET_ENTITY_TYPES_SUMMARY endpoint not configured');
    }

    const response = await axios.post<{ message: EntityTypeSummary[] | { data: EntityTypeSummary[] } }>(
      endpoint,
      {
        active_only: activeOnly ? 'true' : 'false',
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Handle both response formats: direct array or wrapped in data
    const message = response.data.message;
    return Array.isArray(message) ? message : message.data;
  },

  /**
   * Get all entity type categories.
   * Uses ERP endpoint: orgain.kb.api.entity_type_api.get_entity_type_categories
   * Requires: Login
   */
  async getCategories(): Promise<EntityTypeCategory[]> {
    const endpoint = getApiEndpoint('GET_ENTITY_TYPE_CATEGORIES');
    if (!endpoint) {
      throw new Error('GET_ENTITY_TYPE_CATEGORIES endpoint not configured');
    }

    const response = await axios.post<{ message: EntityTypeCategory[] | { data: EntityTypeCategory[] } }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Handle both response formats: direct array or wrapped in data
    const message = response.data.message;
    return Array.isArray(message) ? message : message.data;
  },

  /**
   * Get entity type statistics (database vs registry sync status).
   * Uses ERP endpoint: orgain.kb.api.entity_type_api.get_entity_type_statistics
   * Requires: Login
   */
  async getStatistics(
    scopeToActiveTemplate?: boolean,
    filters?: {
      search?: string;
      category?: string;
      status?: 'all' | 'active' | 'inactive';
      include_system?: boolean;
      has_vocabulary?: 'all' | 'with' | 'without';
    },
  ): Promise<{
    database: Record<string, number>;
    registry: Record<string, number>;
    in_sync: boolean;
  }> {
    const endpoint = getApiEndpoint('GET_ENTITY_TYPE_STATISTICS');
    if (!endpoint) {
      throw new Error('GET_ENTITY_TYPE_STATISTICS endpoint not configured');
    }

    // Keep the summary cards consistent with the row list: pass the same
    // scope AND filters the page is currently showing, so the metrics
    // reflect the active filter set. Omitted keys fall back to backend
    // defaults (scoped to active template, unfiltered).
    const payload: Record<string, string> = {};
    if (scopeToActiveTemplate !== undefined) {
      payload.scope_to_active_template = scopeToActiveTemplate ? 'true' : 'false';
    }
    if (filters?.search) payload.search = filters.search;
    if (filters?.category && filters.category !== 'all') payload.category = filters.category;
    if (filters?.status) payload.status = filters.status;
    if (filters?.include_system !== undefined) {
      payload.include_system = filters.include_system ? 'true' : 'false';
    }
    if (filters?.has_vocabulary && filters.has_vocabulary !== 'all') {
      payload.has_vocabulary = filters.has_vocabulary;
    }

    const response = await axios.post<{ message: {
      database: Record<string, number>;
      registry: Record<string, number>;
      in_sync: boolean;
    } }>(
      endpoint,
      payload,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Get Neo4j entity usage statistics by type.
   * Uses ERP endpoint: orgain.kb.api.entity_type_api.get_neo4j_entity_usage
   * Requires: Login
   */
  async getNeo4jUsage(): Promise<{
    usage_by_type: Record<string, { staging: number; production: number; total: number }>;
    total_types_with_entities: number;
    total_staging_entities: number;
    total_production_entities: number;
  }> {
    const endpoint = getApiEndpoint('GET_NEO4J_ENTITY_USAGE');
    if (!endpoint) {
      throw new Error('GET_NEO4J_ENTITY_USAGE endpoint not configured');
    }

    const response = await axios.post<{ message: {
      usage_by_type: Record<string, { staging: number; production: number; total: number }>;
      total_types_with_entities: number;
      total_staging_entities: number;
      total_production_entities: number;
    } }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Get a single entity type by ID.
   * Uses ERP endpoint: orgain.kb.api.entity_type_api.get_entity_type
   * Requires: Login
   *
   * @param id - The entity type ID
   */
  async getEntityType(id: number): Promise<EntityType> {
    const endpoint = getApiEndpoint('GET_ENTITY_TYPE');
    if (!endpoint) {
      throw new Error('GET_ENTITY_TYPE endpoint not configured');
    }

    const response = await axios.post<{ message: EntityType }>(
      endpoint,
      { id },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Create a new entity type.
   * Uses ERP endpoint: orgain.kb.api.entity_type_api.create_entity_type
   * Requires: Login (admin)
   *
   * @param data - Entity type creation data
   */
  async createEntityType(data: EntityTypeCreate): Promise<EntityType> {
    const endpoint = getApiEndpoint('CREATE_ENTITY_TYPE');
    if (!endpoint) {
      throw new Error('CREATE_ENTITY_TYPE endpoint not configured');
    }

    const response = await axios.post<{ message: EntityType }>(
      endpoint,
      {
        name: data.name,
        code: data.code,
        label: data.label,
        description: data.description,
        category: data.category,
        parent_type: data.parent_type,
        importance_weight: data.importance_weight,
        patterns: data.patterns ? JSON.stringify(data.patterns) : undefined,
        vocabulary: data.vocabulary ? JSON.stringify(data.vocabulary) : undefined,
        is_active: data.is_active,
        is_universal: data.is_universal,
        target_template: data.target_template,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Update an existing entity type.
   * Uses ERP endpoint: orgain.kb.api.entity_type_api.update_entity_type
   * Requires: Login (admin)
   *
   * @param id - The entity type ID to update
   * @param data - Entity type update data
   * @param cascadeNeo4j - Whether to cascade changes to Neo4j (default: false)
   */
  async updateEntityType(
    id: number,
    data: EntityTypeUpdate,
    cascadeNeo4j: boolean = false
  ): Promise<EntityType> {
    const endpoint = getApiEndpoint('UPDATE_ENTITY_TYPE');
    if (!endpoint) {
      throw new Error('UPDATE_ENTITY_TYPE endpoint not configured');
    }

    const response = await axios.post<{ message: EntityType }>(
      endpoint,
      {
        entity_type_id: id,
        name: data.name,
        code: data.code,
        label: data.label,
        description: data.description,
        category: data.category,
        parent_type: data.parent_type,
        importance_weight: data.importance_weight,
        patterns: data.patterns !== undefined ? JSON.stringify(data.patterns) : undefined,
        vocabulary: data.vocabulary !== undefined ? JSON.stringify(data.vocabulary) : undefined,
        is_active: data.is_active,
        is_universal: data.is_universal,
        collapse_to_parent: data.collapse_to_parent,
        cascade_neo4j: cascadeNeo4j,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Delete an entity type by ID.
   * Uses ERP endpoint: orgain.kb.api.entity_type_api.delete_entity_type
   * Requires: Login (admin)
   *
   * @param id - The entity type ID to delete
   * @param hardDelete - Permanently delete instead of soft delete (default: false)
   */
  async deleteEntityType(id: number, hardDelete: boolean = true): Promise<{ message: string }> {
    const endpoint = getApiEndpoint('DELETE_ENTITY_TYPE');
    if (!endpoint) {
      throw new Error('DELETE_ENTITY_TYPE endpoint not configured');
    }

    const response = await axios.post<{ message: { message: string } | string }>(
      endpoint,
      {
        entity_type_id: id,
        hard_delete: hardDelete,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Handle both response formats
    const message = response.data.message;
    return typeof message === 'string' ? { message } : message;
  },

  /**
   * Reload the entity type registry from the database.
   * Uses ERP endpoint: orgain.kb.api.entity_type_api.reload_entity_type_registry
   * Requires: Login (admin)
   */
  async reloadRegistry(): Promise<{
    success: boolean;
    message: string;
    statistics: Record<string, number>;
  }> {
    const endpoint = getApiEndpoint('RELOAD_ENTITY_TYPE_REGISTRY');
    if (!endpoint) {
      throw new Error('RELOAD_ENTITY_TYPE_REGISTRY endpoint not configured');
    }

    const response = await axios.post<{ message: {
      success: boolean;
      message: string;
      statistics: Record<string, number>;
    } }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },
};

// ============================================================================
// Brain Settings types
// ============================================================================

export type ProviderSlug = 'openai' | 'anthropic' | 'gemini';

export type LlmRole =
  | 'extraction'
  | 'chat'
  | 'chat_title'
  | 'agent'
  | 'relationship'
  | 'eval';

export type KeySource =
  | 'llm_provider'
  | 'site_config'
  | 'gemini_settings'
  | 'env'
  | null;

export interface ProviderKeyDescriptor {
  is_set: boolean;
  last4: string | null;
  // The layer the LLM client factory is actually using for this
  // provider - informational, lets the UI render an "effective
  // source" chip so operators understand where the key is coming
  // from when one is configured in more than one place.
  source: KeySource;
}

export interface BrainSettingsPayload {
  // Industry / domain
  domain_knowledge_template?: string | null;
  custom_domain_prompt?: string | null;
  // Extraction
  default_extraction_method?: string | null;
  llm_extraction_provider?: ProviderSlug | string | null;
  llm_extraction_model?: string | null;
  llm_extraction_temperature?: number | null;
  // LLM routing per role
  chat_provider?: ProviderSlug | string | null;
  chat_model?: string | null;
  chat_title_provider?: ProviderSlug | string | null;
  chat_title_model?: string | null;
  agent_provider?: ProviderSlug | string | null;
  agent_model?: string | null;
  relationship_extraction_provider?: ProviderSlug | string | null;
  relationship_extraction_model?: string | null;
  eval_provider?: ProviderSlug | string | null;
  eval_model?: string | null;
  audio_video_model?: string | null;
  max_file_size_mb?: number | null;
  allowed_file_types?: string | null;
  // Notifications - the toggle lives on the Email tab now.
  enable_email_notifications?: boolean;
  // Email configuration (Email tab)
  email_provider_pref?: EmailProviderPref | null;
  email_account?: string | null;
  // Masked descriptors - never includes the plaintext key. Present on
  // GET, omitted on PATCH.
  provider_keys?: Record<ProviderSlug, ProviderKeyDescriptor>;
}

export interface MutationResult {
  ok: boolean;
  name?: string | null;
  error?: string | null;
}

export type EmailProviderPref = 'Gmail' | 'Outlook' | 'IMAP';

export interface EmailAccountSummary {
  name: string;
  email_id?: string;
  service?: string;
  enable_outgoing?: 0 | 1 | boolean;
  default_outgoing?: 0 | 1 | boolean;
}

export interface EmailAccountStatus {
  name: string;
  exists: boolean;
  service?: string;
  email_id?: string;
  smtp_server?: string;
  smtp_port?: number;
  use_ssl?: boolean;
  enable_outgoing?: boolean;
  enable_incoming?: boolean;
  default_outgoing?: boolean;
  awaiting_password?: boolean;
  oauth_connected?: boolean;
  // `modified` of the OAuth token in the Token Cache (ISO string, or null when
  // not connected). Used to tell a fresh authorization from a stale-but-present
  // token on re-authorize - see runEmailOAuthPopup.
  token_updated_at?: string | null;
  auth_method?: string;
  connected_app?: string | null;
  connected_user?: string | null;
}

/** Full editable Email Account - mirrors the native form / Frappe doctype. */
export interface EmailAccount {
  name: string;
  exists?: boolean;
  email_id: string;
  email_account_name?: string;
  service?: string;
  auth_method?: 'Basic' | 'OAuth';
  login_id_is_different?: boolean;
  login_id?: string;
  ascii_encode_password?: boolean;
  has_password?: boolean;
  awaiting_password?: boolean;
  connected_app?: string;
  connected_user?: string;
  oauth_connected?: boolean;
  /**
   * The mailbox identity that actually signed in at the provider, read from the
   * OAuth token. NOT the same as `email_id` (what the operator typed) - showing
   * `email_id` on the connected chip made a wrong-account sign-in look correct.
   * Null on accounts authorized before this was captured.
   */
  oauth_identity?: string | null;
  // Creator-gated (re)authorization: only the admin who created the account
  // may authorize it. `owner` is that admin; `can_authorize` is true only for
  // the current user when they are the owner. Enforced server-side too.
  owner?: string;
  can_authorize?: boolean;
  // Incoming
  enable_incoming?: boolean;
  use_imap?: boolean;
  use_ssl?: boolean;
  use_starttls?: boolean;
  email_sync_option?: string;
  initial_sync_count?: string;
  email_server?: string;
  incoming_port?: string;
  attachment_limit?: number;
  // Outgoing
  enable_outgoing?: boolean;
  default_outgoing?: boolean;
  use_tls?: boolean;
  use_ssl_for_outgoing?: boolean;
  smtp_server?: string;
  smtp_port?: string;
  send_unsubscribe_message?: boolean;
  track_email_status?: boolean;
  no_smtp_authentication?: boolean;
  always_bcc?: string;
  // Signature / Auto Reply / Footer
  add_signature?: boolean;
  signature?: string;
  enable_auto_reply?: boolean;
  auto_reply_message?: string;
  footer?: string;
}

/** Payload for save_email_account - `password` is write-only. */
export type SaveEmailAccountPayload = Partial<Omit<EmailAccount, 'name' | 'exists'>> & {
  email_id: string;
  name?: string;
  password?: string;
};

/** Result of a connection test - saved account or unsaved draft. */
export interface EmailTestResult {
  ok: boolean;
  recipient: string | null;
  error: string | null;
}

/** Booleans → 0/1 so Frappe checkboxes round-trip cleanly. */
const emailAccountBody = (payload: SaveEmailAccountPayload): Record<string, unknown> => {
  const body: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (v === undefined) continue;
    body[k] = typeof v === 'boolean' ? (v ? 1 : 0) : v;
  }
  return body;
};

export interface CreateImapResult {
  ok: boolean;
  name: string | null;
  error: string | null;
}

// --- Native in-app OAuth (Connected App + Email Account) -------------------

export interface ConnectedAppSummary {
  name: string;
  provider_name?: string;
  client_id?: string;
}

export interface DomainKnowledgeTemplateSummary {
  name: string;
  display_label?: string;
  industry?: string;
  description?: string;
  is_active?: 0 | 1;
}

// LLM Model registry - admin-managed via the Frappe LLM Model doctype.
// The Brain Settings model dropdown reads from this; ``name`` is the
// wire-level identifier passed to the LLM SDK (e.g. "gpt-4o-mini"), so
// the Select writes ``name`` back into Brain Settings.<model> field.
export interface LlmModelSummary {
  name: string;
  model_name?: string;
  model_id?: string;
  model_type?: string;
  provider?: string;
  enabled?: 0 | 1;
  supports_function_calling?: 0 | 1;
  supports_vision?: 0 | 1;
  context_window?: number;
  max_tokens?: number;
}

export interface TestProviderKeyResult {
  ok: boolean;
  provider: ProviderSlug;
  model?: string;
  latency_ms?: number;
  text?: string;
  error?: string;
}

export interface LlmProviderSummary {
  name: string;
  provider_name?: string;
  provider_type?: string;
  enabled?: 0 | 1;
}

export interface SaveLlmModelPayload {
  model_id: string;
  provider: string;
  name?: string;
  model_name?: string;
  model_type?: string;
  enabled?: boolean;
  max_tokens?: number;
  context_window?: number;
  supports_vision?: boolean;
  supports_function_calling?: boolean;
  supports_streaming?: boolean;
  cost_per_million_input_tokens?: number;
  cost_per_million_output_tokens?: number;
  rate_limit_rpm?: number;
  rate_limit_tpm?: number;
  description?: string;
}

export interface ActiveLlmConfig {
  roles: Record<LlmRole, { provider?: string; model?: string; error?: string }>;
  provider_keys: Record<ProviderSlug, ProviderKeyDescriptor>;
}

// ============================================================================
// Brain Settings Service
//
// NOTE: the export below is named `brainConfigService` rather than
// `brainSettingsService` to avoid a name collision with the legacy
// (misnamed) `brainSettingsService` further down in this file, which
// is actually a KB-categories service. Future cleanup: rename the
// legacy export to something accurate (e.g. `kbCategorySettingsService`)
// and reclaim the `brainSettingsService` identifier here.
// ============================================================================

export const brainConfigService = {
  async get(): Promise<BrainSettingsPayload> {
    const endpoint = getApiEndpoint('GET_BRAIN_SETTINGS');
    if (!endpoint) throw new Error('GET_BRAIN_SETTINGS endpoint not configured');
    const response = await axios.get<{ message: BrainSettingsPayload }>(endpoint, {
      withCredentials: true,
    });
    return response.data.message;
  },

  async update(patch: BrainSettingsPayload): Promise<BrainSettingsPayload> {
    const endpoint = getApiEndpoint('UPDATE_BRAIN_SETTINGS');
    if (!endpoint) throw new Error('UPDATE_BRAIN_SETTINGS endpoint not configured');
    // Strip the masked `provider_keys` block - that's a read-only
    // surface, the API rejects unknown fields silently but it's
    // clearer to omit them on the wire.
    const { provider_keys, ...patchable } = patch;
    void provider_keys;
    const response = await axios.post<{
      message: { ok: boolean; updated_fields: string[]; settings: BrainSettingsPayload };
    }>(endpoint, patchable, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data.message.settings;
  },

  async updateApiKey(
    provider: ProviderSlug,
    apiKey: string,
  ): Promise<{ ok: boolean; provider: ProviderSlug; key: ProviderKeyDescriptor }> {
    const endpoint = getApiEndpoint('UPDATE_PROVIDER_API_KEY');
    if (!endpoint) throw new Error('UPDATE_PROVIDER_API_KEY endpoint not configured');
    const response = await axios.post<{
      message: { ok: boolean; provider: ProviderSlug; key: ProviderKeyDescriptor };
    }>(
      endpoint,
      { provider, api_key: apiKey },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return response.data.message;
  },

  async testApiKey(provider: ProviderSlug): Promise<TestProviderKeyResult> {
    const endpoint = getApiEndpoint('TEST_PROVIDER_API_KEY');
    if (!endpoint) throw new Error('TEST_PROVIDER_API_KEY endpoint not configured');
    const response = await axios.post<{ message: TestProviderKeyResult }>(
      endpoint,
      { provider },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return response.data.message;
  },

  async listLlmModels(
    provider?: string,
    enabledOnly = true,
  ): Promise<LlmModelSummary[]> {
    const endpoint = getApiEndpoint('LIST_LLM_MODELS');
    if (!endpoint) throw new Error('LIST_LLM_MODELS endpoint not configured');
    const response = await axios.get<{ message: LlmModelSummary[] }>(endpoint, {
      params: {
        provider: provider || '',
        enabled_only: enabledOnly ? 'true' : 'false',
      },
      withCredentials: true,
    });
    return response.data.message || [];
  },

  async listLlmProviders(): Promise<LlmProviderSummary[]> {
    const endpoint = getApiEndpoint('LIST_LLM_PROVIDERS');
    if (!endpoint) throw new Error('LIST_LLM_PROVIDERS endpoint not configured');
    const response = await axios.get<{ message: LlmProviderSummary[] }>(endpoint, {
      withCredentials: true,
    });
    return response.data.message || [];
  },

  async saveLlmModel(payload: SaveLlmModelPayload): Promise<MutationResult> {
    const endpoint = getApiEndpoint('SAVE_LLM_MODEL');
    if (!endpoint) throw new Error('SAVE_LLM_MODEL endpoint not configured');
    const body: Record<string, unknown> = {
      model_id: payload.model_id,
      provider: payload.provider,
      model_name: payload.model_name ?? '',
      model_type: payload.model_type ?? 'Text',
    };
    if (payload.name) body.name = payload.name;
    if (payload.enabled !== undefined) body.enabled = payload.enabled ? 1 : 0;
    if (payload.max_tokens !== undefined) body.max_tokens = payload.max_tokens;
    if (payload.context_window !== undefined) body.context_window = payload.context_window;
    if (payload.supports_vision !== undefined) body.supports_vision = payload.supports_vision ? 1 : 0;
    if (payload.supports_function_calling !== undefined) {
      body.supports_function_calling = payload.supports_function_calling ? 1 : 0;
    }
    if (payload.supports_streaming !== undefined) {
      body.supports_streaming = payload.supports_streaming ? 1 : 0;
    }
    if (payload.cost_per_million_input_tokens !== undefined) {
      body.cost_per_million_input_tokens = payload.cost_per_million_input_tokens;
    }
    if (payload.cost_per_million_output_tokens !== undefined) {
      body.cost_per_million_output_tokens = payload.cost_per_million_output_tokens;
    }
    if (payload.rate_limit_rpm !== undefined) body.rate_limit_rpm = payload.rate_limit_rpm;
    if (payload.rate_limit_tpm !== undefined) body.rate_limit_tpm = payload.rate_limit_tpm;
    if (payload.description !== undefined) body.description = payload.description;
    const response = await axios.post<{ message: MutationResult }>(endpoint, body, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data.message;
  },

  async listIndustries(activeOnly = true): Promise<DomainKnowledgeTemplateSummary[]> {
    const endpoint = getApiEndpoint('LIST_DOMAIN_KNOWLEDGE_TEMPLATES');
    if (!endpoint) throw new Error('LIST_DOMAIN_KNOWLEDGE_TEMPLATES endpoint not configured');
    const response = await axios.get<{ message: DomainKnowledgeTemplateSummary[] }>(endpoint, {
      params: { active_only: activeOnly ? 'true' : 'false' },
      withCredentials: true,
    });
    return response.data.message;
  },

  async describeActiveConfig(): Promise<ActiveLlmConfig> {
    const endpoint = getApiEndpoint('DESCRIBE_ACTIVE_LLM_CONFIG');
    if (!endpoint) throw new Error('DESCRIBE_ACTIVE_LLM_CONFIG endpoint not configured');
    const response = await axios.get<{ message: ActiveLlmConfig }>(endpoint, {
      withCredentials: true,
    });
    return response.data.message;
  },

  // ---------------------------------------------------------------------
  // Email tab - outgoing Email Account integration
  // ---------------------------------------------------------------------

  async listEmailAccounts(outgoingOnly = true): Promise<EmailAccountSummary[]> {
    const endpoint = getApiEndpoint('LIST_EMAIL_ACCOUNTS');
    if (!endpoint) throw new Error('LIST_EMAIL_ACCOUNTS endpoint not configured');
    const response = await axios.get<{ message: EmailAccountSummary[] }>(endpoint, {
      params: { outgoing_only: outgoingOnly ? 'true' : 'false' },
      withCredentials: true,
      // Never serve a stale list from the browser cache - a freshly created
      // account must show up immediately without a full page reload.
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    return response.data.message;
  },

  async getEmailAccountStatus(name: string): Promise<EmailAccountStatus> {
    const endpoint = getApiEndpoint('GET_EMAIL_ACCOUNT_STATUS');
    if (!endpoint) throw new Error('GET_EMAIL_ACCOUNT_STATUS endpoint not configured');
    const response = await axios.get<{ message: EmailAccountStatus }>(endpoint, {
      params: { name },
      withCredentials: true,
    });
    return response.data.message;
  },

  async getEmailAccount(name: string): Promise<EmailAccount> {
    const endpoint = getApiEndpoint('GET_EMAIL_ACCOUNT');
    if (!endpoint) throw new Error('GET_EMAIL_ACCOUNT endpoint not configured');
    const response = await axios.get<{ message: EmailAccount }>(endpoint, {
      params: { name },
      withCredentials: true,
    });
    return response.data.message;
  },

  async saveEmailAccount(payload: SaveEmailAccountPayload): Promise<CreateImapResult> {
    const endpoint = getApiEndpoint('SAVE_EMAIL_ACCOUNT');
    if (!endpoint) throw new Error('SAVE_EMAIL_ACCOUNT endpoint not configured');
    const response = await axios.post<{ message: CreateImapResult }>(
      endpoint,
      emailAccountBody(payload),
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    );
    return response.data.message;
  },

  async testEmailAccount(name: string): Promise<EmailTestResult> {
    const endpoint = getApiEndpoint('TEST_EMAIL_ACCOUNT');
    if (!endpoint) throw new Error('TEST_EMAIL_ACCOUNT endpoint not configured');
    const response = await axios.post<{ message: EmailTestResult }>(
      endpoint,
      { name },
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    );
    return response.data.message;
  },

  /**
   * Test the draft form values without saving them. `name` (when the account
   * already exists) lets the backend fall back to the stored OAuth token or an
   * unchanged password; everything else is tested exactly as typed.
   */
  async testEmailDraft(payload: SaveEmailAccountPayload): Promise<EmailTestResult> {
    const endpoint = getApiEndpoint('TEST_EMAIL_DRAFT');
    if (!endpoint) throw new Error('TEST_EMAIL_DRAFT endpoint not configured');
    const response = await axios.post<{ message: EmailTestResult }>(
      endpoint,
      emailAccountBody(payload),
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    );
    return response.data.message;
  },

  async deleteEmailAccount(name: string): Promise<CreateImapResult> {
    const endpoint = getApiEndpoint('DELETE_EMAIL_ACCOUNT');
    if (!endpoint) throw new Error('DELETE_EMAIL_ACCOUNT endpoint not configured');
    const response = await axios.post<{ message: CreateImapResult }>(
      endpoint,
      { name },
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    );
    return response.data.message;
  },

  // --- Native in-app OAuth (Connected App + Email Account) -----------------

  async listConnectedApps(): Promise<ConnectedAppSummary[]> {
    const endpoint = getApiEndpoint('LIST_CONNECTED_APPS');
    if (!endpoint) throw new Error('LIST_CONNECTED_APPS endpoint not configured');
    const response = await axios.get<{ message: ConnectedAppSummary[] }>(endpoint, {
      withCredentials: true,
    });
    return response.data.message || [];
  },

  async authorizeEmailAccount(name: string): Promise<{ authorize_url: string }> {
    const endpoint = getApiEndpoint('AUTHORIZE_EMAIL_ACCOUNT');
    if (!endpoint) throw new Error('AUTHORIZE_EMAIL_ACCOUNT endpoint not configured');
    const response = await axios.post<{ message: { authorize_url: string } }>(
      endpoint,
      { name },
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    );
    return response.data.message;
  },

  /**
   * Confirm a mismatched sign-in: repoint the mailbox at the identity that
   * actually consented and redeem the parked token. Only ever called from the
   * explicit "Use <identity>" confirmation - this rewrites the address the
   * account sends from.
   */
  async adoptEmailOAuthIdentity(
    name: string,
    confirmToken: string,
  ): Promise<{ ok: boolean; email_id: string; identity: string }> {
    const endpoint = getApiEndpoint('ADOPT_EMAIL_OAUTH_IDENTITY');
    if (!endpoint) throw new Error('ADOPT_EMAIL_OAUTH_IDENTITY endpoint not configured');
    const response = await axios.post<{ message: { ok: boolean; email_id: string; identity: string } }>(
      endpoint,
      { name, confirm_token: confirmToken },
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    );
    return response.data.message;
  },

};

/**
 * Drive the email OAuth consent popup.
 *
 * Opens the provider's authorize URL (accounts.google.com /
 * login.microsoftonline.com) in a popup. Frappe's callback exchanges the code,
 * stores the token, then redirects the popup to our `email_oauth_complete`
 * page which posts `{ type: 'orgain.email_oauth', status: 'success' }` back and
 * closes. We also verify via the status endpoint when the popup closes, so a
 * blocked postMessage doesn't strand the flow.
 */
export interface EmailOAuthResult {
  ok: boolean;
  /** Reactive admin-consent (Option B): the tenant admin must approve first. */
  adminConsent: boolean;
  message?: string;
  /**
   * The operator consented as a different mailbox than the account is set to
   * (signed in as crm@, account is anandha@). NOT a failure and NOT applied -
   * the token is parked server-side and nothing has changed. The caller prompts
   * to adopt `identity`, then redeems `confirmToken` via adoptEmailOAuthIdentity.
   */
  identityMismatch?: boolean;
  /** Who actually signed in (from the token). */
  identity?: string;
  /** The mailbox address the account currently carries. */
  expected?: string;
  /** One-shot handle for the parked token; expires in 10 minutes. */
  confirmToken?: string;
}

export async function runEmailOAuthPopup(
  authorizeUrl: string,
  accountName: string,
  opts: { timeoutMs?: number } = {},
): Promise<EmailOAuthResult> {
  const timeoutMs = opts.timeoutMs ?? 5 * 60 * 1000;
  const popup = window.open(
    authorizeUrl,
    'email-oauth',
    'width=620,height=720,menubar=no,toolbar=no,location=yes,status=no',
  );
  if (!popup) {
    throw new Error('Popup blocked. Please allow popups for this site and try again.');
  }

  // Snapshot the token's freshness BEFORE the user consents. On a re-authorize
  // the account is already connected, so `oauth_connected` staying true is NOT
  // proof this attempt worked (a wrong Client ID makes the provider show an
  // error page that never redirects back to us - no success message is posted,
  // and the OLD token is still present). Only a NEWER token timestamp proves a
  // fresh token was minted. `undefined` = couldn't read it; fall back to the
  // plain connected check in that case.
  let baselineToken: string | null | undefined;
  try {
    const s = await brainConfigService.getEmailAccountStatus(accountName);
    baselineToken = s.token_updated_at ?? null;
  } catch {
    baselineToken = undefined;
  }

  const started = Date.now();
  return new Promise<EmailOAuthResult>((resolve, reject) => {
    let settled = false;
    let timer: number | null = null;

    const cleanup = () => {
      if (timer !== null) window.clearInterval(timer);
      window.removeEventListener('message', onMessage);
    };
    const verifyAndFinish = async () => {
      try {
        const status = await brainConfigService.getEmailAccountStatus(accountName);
        if (!status.oauth_connected) return false;
        // When we have a baseline, the token must have been RE-ISSUED (its
        // timestamp advanced) for this attempt to count as success - otherwise
        // a failed re-authorize would look successful because the pre-existing
        // token is still there. With no baseline (first-time connect, or the
        // status read failed), oauth_connected flipping to true is reliable.
        if (baselineToken !== undefined && status.token_updated_at) {
          return status.token_updated_at !== baselineToken;
        }
        return true;
      } catch {
        return false;
      }
    };
    const finishOk = () => {
      if (settled) return;
      settled = true;
      cleanup();
      try { popup.close(); } catch { /* swallow */ }
      resolve({ ok: true, adminConsent: false });
    };
    // Admin consent required is NOT a failure - resolve (don't reject) so the
    // caller shows a "waiting for your admin" notice with a retry.
    const finishConsent = (message?: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      try { popup.close(); } catch { /* swallow */ }
      resolve({ ok: false, adminConsent: true, message });
    };
    const finishErr = (err: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      try { popup.close(); } catch { /* swallow */ }
      reject(err);
    };

    // Wrong account signed in. Resolve (don't reject): nothing was applied, and
    // the caller offers to adopt that identity instead of erroring at the user.
    const finishMismatch = (data: Record<string, unknown>) => {
      if (settled) return;
      settled = true;
      cleanup();
      try { popup.close(); } catch { /* swallow */ }
      resolve({
        ok: false,
        adminConsent: false,
        identityMismatch: true,
        identity: String(data.identity || ''),
        expected: String(data.expected || ''),
        confirmToken: String(data.confirm_token || ''),
        message: data.message ? String(data.message) : undefined,
      });
    };

    const onMessage = async (ev: MessageEvent) => {
      const data = ev?.data;
      if (!data || typeof data !== 'object' || data.type !== 'orgain.email_oauth') return;
      if (data.status === 'success') {
        // Trust but verify - confirm the token actually landed.
        finishOk();
      } else if (data.identity_mismatch) {
        finishMismatch(data);
      } else if (data.admin_consent) {
        finishConsent(data.message);
      } else {
        finishErr(new Error(data.message || 'Email OAuth failed'));
      }
    };
    window.addEventListener('message', onMessage);

    timer = window.setInterval(async () => {
      if (popup.closed) {
        const ok = await verifyAndFinish();
        if (ok) finishOk();
        // The popup closed without a confirmed new token. This covers a plain
        // cancel AND a provider-side rejection (e.g. a wrong Client ID shows an
        // error page that never redirects back, so no message is posted). Give
        // an actionable message rather than a bare "cancelled".
        else
          finishErr(
            new Error(
              "Authorization didn't complete. Please try again - and if you " +
                'changed the Connected App, check its Client ID and Secret.',
            ),
          );
        return;
      }
      if (Date.now() - started > timeoutMs) {
        finishErr(new Error('OAuth timed out'));
      }
    }, 1500);
  });
}

// ============================================================================
// Video Processing Service (COMMENTED OUT - unused)
// ============================================================================

// export interface VideoUploadResponse {
//   success: boolean;
//   task_id: string;
//   ingestion_id: string;
//   filename: string;
//   file_size_mb: number;
//   message: string;
// }

// export interface VideoTaskStatus {
//   task_id: string;
//   status: string;
//   stage?: string;
//   progress?: number;
//   message?: string;
//   result?: {
//     success: boolean;
//     document_id?: string;
//     processing_stats?: {
//       duration_seconds: number;
//       chunks_created: number;
//       embeddings_generated: number;
//       processing_time_seconds: number;
//       transcript_segments: number;
//     };
//     analysis?: {
//       title_suggestion: string;
//       summary: string;
//       main_topics: string[];
//       suggested_tags: string[];
//     };
//   };
// }

// export const brainVideoService = {
//   async uploadVideo(
//     file: File,
//     metadata?: {
//       title?: string;
//       description?: string;
//       category?: string;
//       tags?: string[];
//       department?: string;
//       access_level?: string;
//       language?: string;
//     }
//   ): Promise<VideoUploadResponse> {
//     const uploadResult = await fileUpload(file, 500, { folderToBeUploaded: 'knowledgebase', isPrivate: true });
//     if (uploadResult.error || !uploadResult.url) {
//       throw new Error(uploadResult.error || 'Failed to upload video file');
//     }
//     const endpoint = getApiEndpoint('KB_VIDEO_UPLOAD');
//     if (!endpoint) {
//       throw new Error('KB_VIDEO_UPLOAD endpoint not configured');
//     }
//     const response = await axios.post<{ message: VideoUploadResponse }>(
//       endpoint,
//       {
//         file_url: uploadResult.url,
//         title: metadata?.title,
//         description: metadata?.description,
//         category: metadata?.category,
//         tags: metadata?.tags ? JSON.stringify(metadata.tags) : undefined,
//         department: metadata?.department,
//         access_level: metadata?.access_level,
//         language: metadata?.language,
//       },
//       {
//         withCredentials: true,
//         headers: { 'Content-Type': 'application/json' },
//         timeout: 120000,
//       }
//     );
//     return response.data.message;
//   },
//   async getTaskStatus(taskId: string): Promise<VideoTaskStatus> {
//     const endpoint = getApiEndpoint('KB_VIDEO_TASK_STATUS');
//     if (!endpoint) {
//       throw new Error('KB_VIDEO_TASK_STATUS endpoint not configured');
//     }
//     const response = await axios.post<{ message: VideoTaskStatus }>(
//       endpoint,
//       { task_id: taskId },
//       {
//         withCredentials: true,
//         headers: { 'Content-Type': 'application/json' },
//       }
//     );
//     return response.data.message;
//   },
//   async getHealth(): Promise<{
//     status: string;
//     gemini_available: boolean;
//     supported_formats: string[];
//     max_file_size_mb: number;
//   }> {
//     const endpoint = getApiEndpoint('KB_VIDEO_HEALTH');
//     if (!endpoint) {
//       throw new Error('KB_VIDEO_HEALTH endpoint not configured');
//     }
//     const response = await axios.post<{ message: {
//       status: string;
//       gemini_available: boolean;
//       supported_formats: string[];
//       max_file_size_mb: number;
//     } }>(
//       endpoint,
//       {},
//       {
//         withCredentials: true,
//         headers: { 'Content-Type': 'application/json' },
//       }
//     );
//     return response.data.message;
//   },
// };

// ============================================================================
// Audio Processing Service (COMMENTED OUT - unused)
// ============================================================================

// export interface AudioUploadResponse {
//   success: boolean;
//   task_id: string;
//   ingestion_id: string;
//   filename: string;
//   file_size_mb: number;
//   message: string;
// }

// export interface AudioTaskStatus {
//   task_id: string;
//   status: string;
//   stage?: string;
//   progress?: number;
//   message?: string;
//   speakers_identified?: number;
//   result?: {
//     success: boolean;
//     document_id?: string;
//     processing_stats?: {
//       duration_seconds: number;
//       chunks_created: number;
//       embeddings_generated: number;
//       processing_time_seconds: number;
//       transcript_segments: number;
//       speakers_identified: number;
//     };
//     analysis?: {
//       title_suggestion: string;
//       summary: string;
//       main_topics: string[];
//       suggested_tags: string[];
//       speakers_detected: number;
//       content_type: string;
//     };
//   };
// }

// export const brainAudioService = {
//   async uploadAudio(
//     file: File,
//     metadata?: {
//       title?: string;
//       description?: string;
//       category?: string;
//       tags?: string[];
//       department?: string;
//       access_level?: string;
//       language?: string;
//       enable_speaker_diarization?: boolean;
//     }
//   ): Promise<AudioUploadResponse> {
//     const uploadResult = await fileUpload(file, 200, { folderToBeUploaded: 'knowledgebase', isPrivate: true });
//     if (uploadResult.error || !uploadResult.url) {
//       throw new Error(uploadResult.error || 'Failed to upload audio file');
//     }
//     const endpoint = getApiEndpoint('KB_AUDIO_UPLOAD');
//     if (!endpoint) {
//       throw new Error('KB_AUDIO_UPLOAD endpoint not configured');
//     }
//     const response = await axios.post<{ message: AudioUploadResponse }>(
//       endpoint,
//       {
//         file_url: uploadResult.url,
//         title: metadata?.title,
//         description: metadata?.description,
//         category: metadata?.category,
//         tags: metadata?.tags ? JSON.stringify(metadata.tags) : undefined,
//         department: metadata?.department,
//         access_level: metadata?.access_level,
//         language: metadata?.language,
//         enable_speaker_diarization: metadata?.enable_speaker_diarization ?? true,
//       },
//       {
//         withCredentials: true,
//         headers: { 'Content-Type': 'application/json' },
//         timeout: 180000,
//       }
//     );
//     return response.data.message;
//   },
//   async getTaskStatus(taskId: string): Promise<AudioTaskStatus> {
//     const endpoint = getApiEndpoint('KB_AUDIO_TASK_STATUS');
//     if (!endpoint) {
//       throw new Error('KB_AUDIO_TASK_STATUS endpoint not configured');
//     }
//     const response = await axios.post<{ message: AudioTaskStatus }>(
//       endpoint,
//       { task_id: taskId },
//       {
//         withCredentials: true,
//         headers: { 'Content-Type': 'application/json' },
//       }
//     );
//     return response.data.message;
//   },
//   async getHealth(): Promise<{
//     status: string;
//     gemini_available: boolean;
//     supported_formats: string[];
//     format_count: number;
//     speaker_diarization: boolean;
//     max_file_size_mb: number;
//     max_duration_hours: number;
//   }> {
//     const endpoint = getApiEndpoint('KB_AUDIO_HEALTH');
//     if (!endpoint) {
//       throw new Error('KB_AUDIO_HEALTH endpoint not configured');
//     }
//     const response = await axios.post<{ message: {
//       status: string;
//       gemini_available: boolean;
//       supported_formats: string[];
//       format_count: number;
//       speaker_diarization: boolean;
//       max_file_size_mb: number;
//       max_duration_hours: number;
//     } }>(
//       endpoint,
//       {},
//       {
//         withCredentials: true,
//         headers: { 'Content-Type': 'application/json' },
//       }
//     );
//     return response.data.message;
//   },
// };

// ============================================================================
// Settings Service
// ============================================================================

export interface SettingsItem {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  created_by?: string;
}

export interface Category extends SettingsItem {
  code: string;
  color: string;
}

export interface Department extends SettingsItem {
  code?: string;
  department_name?: string;
  is_default?: boolean | number;
}

export interface Language extends SettingsItem {
  code: string;
}

export interface DocumentTypeSettings extends SettingsItem {
  file_extensions?: string;
}

export interface AccessLevel extends SettingsItem {
  level: number;
  level_name?: string;
  is_default?: boolean | number;
}

export const brainSettingsService = {
  /**
   * Get categories.
   * Uses ERP endpoint: orgain.kb.api.settings_api.get_categories
   */
  async getCategories(): Promise<Category[]> {
    const endpoint = getApiEndpoint('KB_SETTINGS_CATEGORIES');
    if (!endpoint) {
      throw new Error('KB_SETTINGS_CATEGORIES endpoint not configured');
    }

    const response = await axios.post<{ message: any }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const message = response.data.message;
    // Handle {status, categories, total} response from admin_api.get_categories
    if (message?.categories && Array.isArray(message.categories)) {
      return message.categories
        .filter((cat: any) => cat.category_name || cat.name)
        .map((cat: any, index: number) => ({
          ...cat,
          id: index + 1,
          name: cat.category_name || cat.name,
          code: cat.code || cat.category_name || cat.name,
          color: cat.color || '#1890ff',
          is_active: !!cat.is_active,
        }));
    }
    if (Array.isArray(message)) return message;
    if (message?.data && Array.isArray(message.data)) return message.data;
    return [];
  },

  /**
   * Get departments.
   * Uses ERP endpoint: orgain.kb.api.settings_api.get_departments
   */
  async getDepartments(): Promise<Department[]> {
    const endpoint = getApiEndpoint('KB_SETTINGS_DEPARTMENTS');
    if (!endpoint) {
      throw new Error('KB_SETTINGS_DEPARTMENTS endpoint not configured');
    }

    const response = await axios.post<{ message: any }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const message = response.data.message;
    // Handle {status, departments, total} response from admin_api.get_departments
    if (message?.departments && Array.isArray(message.departments)) {
      return message.departments
        .filter((dept: any) => dept.department_name || dept.name)
        .map((dept: any, index: number) => ({
          ...dept,
          id: index + 1,
          name: dept.department_name || dept.name,
          code: dept.code || dept.department_name || dept.name,
          department_name: dept.department_name,
          is_active: !!dept.is_active,
          is_default: dept.is_default,
        }));
    }
    if (Array.isArray(message)) return message;
    if (message?.data && Array.isArray(message.data)) return message.data;
    return [];
  },

  /**
   * Get languages.
   * Uses ERP endpoint: orgain.kb.api.settings_api.get_languages
   */
  async getLanguages(): Promise<Language[]> {
    const endpoint = getApiEndpoint('KB_SETTINGS_LANGUAGES');
    if (!endpoint) {
      throw new Error('KB_SETTINGS_LANGUAGES endpoint not configured');
    }

    const response = await axios.post<{ message: any }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const message = response.data.message;
    // Handle {status, languages, total} response from admin_api.get_languages
    if (message?.languages && Array.isArray(message.languages)) {
      return message.languages
        .filter((lang: any) => lang.language_name || lang.name)
        .map((lang: any, index: number) => ({
          ...lang,
          id: index + 1,
          name: lang.language_name || lang.name,
          code: lang.code || lang.language_name || lang.name,
          is_active: !!lang.is_active,
        }));
    }
    if (Array.isArray(message)) return message;
    if (message?.data && Array.isArray(message.data)) return message.data;
    return [];
  },

  /**
   * Get document types.
   * Uses ERP endpoint: orgain.kb.api.settings_api.get_document_types
   */
  async getDocumentTypes(): Promise<DocumentTypeSettings[]> {
    const endpoint = getApiEndpoint('KB_SETTINGS_DOCUMENT_TYPES');
    if (!endpoint) {
      throw new Error('KB_SETTINGS_DOCUMENT_TYPES endpoint not configured');
    }

    const response = await axios.post<{ message: any }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const message = response.data.message;
    // Handle {status, document_types, total} response from admin_api.get_document_types
    if (message?.document_types && Array.isArray(message.document_types)) {
      return message.document_types
        .filter((dt: any) => dt.type_name || dt.document_type_name || dt.name)
        .map((dt: any, index: number) => ({
          ...dt,
          id: index + 1,
          name: dt.type_name || dt.document_type_name || dt.name,
          file_extensions: dt.file_extensions || '',
          is_active: !!dt.is_active,
        }));
    }
    if (Array.isArray(message)) return message;
    if (message?.data && Array.isArray(message.data)) return message.data;
    return [];
  },

  /**
   * Get access levels.
   * Uses ERP endpoint: orgain.kb.api.settings_api.get_access_levels
   */
  async getAccessLevels(): Promise<AccessLevel[]> {
    const endpoint = getApiEndpoint('KB_SETTINGS_ACCESS_LEVELS');
    if (!endpoint) {
      throw new Error('KB_SETTINGS_ACCESS_LEVELS endpoint not configured');
    }

    const response = await axios.post<{ message: any }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const message = response.data.message;
    // Handle {status, access_levels, total} response from admin_api.get_access_levels
    if (message?.access_levels && Array.isArray(message.access_levels)) {
      // Deduplicate by level_name (API may return duplicates) and filter empty names
      const seen = new Set<string>();
      return message.access_levels
        .filter((al: any) => {
          const key = al.level_name || al.name;
          if (!key) return false;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((al: any, index: number) => ({
          ...al,
          id: index + 1,
          name: al.level_name || al.name,
          level: Number(al.level) || 0,
          level_name: al.level_name,
          is_active: !!al.is_active,
          is_default: al.is_default,
        }));
    }
    if (Array.isArray(message)) return message;
    if (message?.data && Array.isArray(message.data)) return message.data;
    return [];
  },

  /**
   * Get settings summary.
   * Uses ERP endpoint: orgain.kb.api.settings_api.get_summary
   */
  async getSettingsSummary(): Promise<any> {
    const endpoint = getApiEndpoint('KB_SETTINGS_SUMMARY');
    if (!endpoint) {
      throw new Error('KB_SETTINGS_SUMMARY endpoint not configured');
    }

    const response = await axios.post<{ message: any }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },
};

// ============================================================================
// Embeddings Types
// ============================================================================

export type EmbeddingStatus = 'embedded' | 'missing' | 'failed' | 'pending';

export interface ChunkEmbeddingInfo {
  chunk_id: string;
  chunk_index: number;
  text_preview: string;
  text_length: number;
  token_count?: number;
  status: EmbeddingStatus;
  vector_magnitude?: number;
  embedded_at?: string;
  source_element_type?: string;
  section_title?: string;
  page_number?: number;
}

export interface EmbeddingConfiguration {
  provider: string;
  model_name: string;
  dimensions: number;
  collection_name: string;
  max_tokens?: number;
}

export interface EmbeddingStatistics {
  document_id: string;
  filename: string;
  total_embeddings: number;
  expected_chunks: number;
  missing_embeddings: number;
  coverage_percentage: number;
  quality_score: number;
  avg_vector_magnitude?: number;
  min_vector_magnitude?: number;
  max_vector_magnitude?: number;
  text_chunks: number;
  table_chunks: number;
  image_chunks: number;
  gemini_analyzed_chunks?: number;
  earliest_embedding?: string;
  latest_embedding?: string;
}

export interface EmbeddingQualityMetrics {
  document_id: string;
  coverage_score: number;
  completeness_score: number;
  magnitude_consistency: number;
  anomaly_count: number;
  anomaly_details: string[];
  semantic_coherence?: number;
  status: 'healthy' | 'warning' | 'critical';
  recommendations: string[];
}

export interface DocumentEmbeddingsResponse {
  success: boolean;
  document_id: string;
  filename: string;
  configuration: EmbeddingConfiguration;
  statistics: EmbeddingStatistics;
  quality?: EmbeddingQualityMetrics;
  embeddings: ChunkEmbeddingInfo[];
  total_count: number;
  page: number;
  page_size: number;
  warnings: string[];
}

// ============================================================================
// Embeddings Service
// ============================================================================

export const embeddingsService = {
  /**
   * Get complete embedding information for a document.
   * Uses ERP endpoint: orgain.kb.api.embedding_api.get_document_embeddings
   * Requires: Admin permission (System Manager or Admin role)
   */
  async getDocumentEmbeddings(
    documentId: string,
    page: number = 1,
    pageSize: number = 50,
    includeQuality: boolean = false,
    isStaging: boolean = false
  ): Promise<DocumentEmbeddingsResponse> {
    const endpoint = getApiEndpoint('GET_DOCUMENT_EMBEDDINGS');
    if (!endpoint) {
      throw new Error('GET_DOCUMENT_EMBEDDINGS endpoint not configured');
    }

    const response = await axios.post<{ message: DocumentEmbeddingsResponse }>(
      endpoint,
      {
        document_id: documentId,
        page,
        page_size: pageSize,
        include_quality: includeQuality,
        is_staging: isStaging,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Get quick statistics about document embeddings.
   * Uses ERP endpoint: orgain.kb.api.embedding_api.get_embedding_statistics
   */
  async getEmbeddingStatistics(
    documentId: string,
    isStaging: boolean = false
  ): Promise<{ success: boolean; document_id: string; statistics: EmbeddingStatistics }> {
    const endpoint = getApiEndpoint('GET_EMBEDDING_STATISTICS');
    if (!endpoint) {
      throw new Error('GET_EMBEDDING_STATISTICS endpoint not configured');
    }

    const response = await axios.post<{ message: { success: boolean; document_id: string; statistics: EmbeddingStatistics } }>(
      endpoint,
      {
        document_id: documentId,
        is_staging: isStaging,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Get detailed quality analysis of document embeddings.
   * Uses ERP endpoint: orgain.kb.api.embedding_api.get_embedding_quality
   * Requires: Admin permission
   */
  async getEmbeddingQuality(
    documentId: string,
    isStaging: boolean = false
  ): Promise<{ success: boolean; document_id: string; quality: EmbeddingQualityMetrics }> {
    const endpoint = getApiEndpoint('GET_EMBEDDING_QUALITY');
    if (!endpoint) {
      throw new Error('GET_EMBEDDING_QUALITY endpoint not configured');
    }

    const response = await axios.post<{ message: { success: boolean; document_id: string; quality: EmbeddingQualityMetrics } }>(
      endpoint,
      {
        document_id: documentId,
        is_staging: isStaging,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },
};

// Utility functions for embeddings
export function getEmbeddingStatusColor(status: EmbeddingStatus): string {
  switch (status) {
    case 'embedded': return '#22c55e'; // green
    case 'missing': return '#ef4444'; // red
    case 'failed': return '#f97316'; // orange
    case 'pending': return '#3b82f6'; // blue
    default: return '#6b7280'; // gray
  }
}

export function getQualityStatusColor(status: string): string {
  switch (status) {
    case 'healthy': return '#22c55e'; // green
    case 'warning': return '#eab308'; // yellow
    case 'critical': return '#ef4444'; // red
    default: return '#6b7280'; // gray
  }
}

export function getCoverageColor(coverage: number): string {
  if (coverage >= 100) return '#22c55e'; // green
  if (coverage >= 80) return '#eab308'; // yellow
  return '#ef4444'; // red
}

// ============================================================================
// Knowledge Graph Types
// ============================================================================

export interface GraphNode {
  id: string;
  type: string;
  label: string;
  color?: string;
  size?: number;
  entity_type?: string;
  properties?: Record<string, any>;
  filename?: string;
  document_type?: string;
  entity_value?: string;
  confidence?: number;
}

export interface GraphEdge {
  from: string;
  to: string;
  label: string;
  arrows?: string;
  color?: string;
  width?: number;
  strength?: number;
  confidence?: number;
  properties?: Record<string, any>;
}

export interface GraphStatistics {
  total_nodes: number;
  total_edges: number;
  node_types: Record<string, number>;
  entity_types: Record<string, number>;
  relationship_types: Record<string, number>;
  avg_connections_per_node: number;
  max_connections: number;
  isolated_nodes: number;
}

export interface DocumentGraphResponse {
  success: boolean;
  document_id: string;
  filename?: string;
  center_node_id: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
  statistics: GraphStatistics;
  depth: number;
  filters_applied: string[];
  is_staging?: boolean;
  staging_message?: string;
  staging_entity_count?: number;
}

export interface GraphQueryParams {
  depth?: number;
  entity_types?: string[];
  min_confidence?: number;
  include_chunks?: boolean;
  max_nodes?: number;
  is_staging?: boolean;
}

// Node color scheme
export const GRAPH_NODE_COLORS: Record<string, string> = {
  Document: '#1890ff',
  Entity: '#52c41a',
  PERSON: '#eb2f96',
  ORGANIZATION: '#fa8c16',
  LOCATION: '#a0d911',
  DATE: '#ff7a45',
  MONEY: '#722ed1',
  PRODUCT: '#13c2c2',
  EVENT: '#f5222d',
  TAG: '#595959',
  TOPIC: '#2f54eb',
  Chunk: '#bfbfbf',
  default: '#8c8c8c',
};

// ============================================================================
// Knowledge Graph Service
// ============================================================================

export const graphService = {
  /**
   * Get complete knowledge graph for a document (vis-network format).
   * Uses ERP endpoint: orgain.kb.api.graph_api.get_document_graph
   */
  async getDocumentGraph(
    documentId: string,
    params?: GraphQueryParams
  ): Promise<DocumentGraphResponse> {
    const endpoint = getApiEndpoint('GET_DOCUMENT_GRAPH');
    if (!endpoint) {
      throw new Error('GET_DOCUMENT_GRAPH endpoint not configured');
    }

    // Format entity_types as JSON array or comma-separated string
    const entityTypes = params?.entity_types?.length
      ? JSON.stringify(params.entity_types)
      : undefined;

    const response = await axios.post<{ message: DocumentGraphResponse }>(
      endpoint,
      {
        document_id: documentId,
        depth: params?.depth ?? 1,
        entity_types: entityTypes,
        min_confidence: params?.min_confidence ?? 0.0,
        include_chunks: params?.include_chunks ?? false,
        max_nodes: params?.max_nodes ?? 100,
        is_staging: params?.is_staging ?? false,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },

  /**
   * Get quick statistics for document knowledge graph.
   * Uses ERP endpoint: orgain.kb.api.graph_api.get_graph_statistics
   * Requires: Login
   */
  async getGraphStatistics(documentId: string): Promise<{
    success: boolean;
    document_id: string;
    statistics: GraphStatistics;
  }> {
    const endpoint = getApiEndpoint('GET_GRAPH_STATISTICS');
    if (!endpoint) {
      throw new Error('GET_GRAPH_STATISTICS endpoint not configured');
    }

    const response = await axios.post<{ message: {
      success: boolean;
      document_id: string;
      statistics: GraphStatistics;
    } }>(
      endpoint,
      {
        document_id: documentId,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },
};

// Utility function to get node color
export function getGraphNodeColor(type: string, entityType?: string): string {
  if (entityType && GRAPH_NODE_COLORS[entityType]) {
    return GRAPH_NODE_COLORS[entityType];
  }
  return GRAPH_NODE_COLORS[type] || GRAPH_NODE_COLORS.default;
}

// Get legend items for knowledge graph
export function getGraphLegendItems(): Array<{ type: string; color: string; label: string }> {
  return [
    { type: 'Document', color: GRAPH_NODE_COLORS.Document, label: 'Document' },
    { type: 'PERSON', color: GRAPH_NODE_COLORS.PERSON, label: 'Person' },
    { type: 'ORGANIZATION', color: GRAPH_NODE_COLORS.ORGANIZATION, label: 'Organization' },
    { type: 'LOCATION', color: GRAPH_NODE_COLORS.LOCATION, label: 'Location' },
    { type: 'DATE', color: GRAPH_NODE_COLORS.DATE, label: 'Date' },
    { type: 'MONEY', color: GRAPH_NODE_COLORS.MONEY, label: 'Money' },
    { type: 'Entity', color: GRAPH_NODE_COLORS.Entity, label: 'Other Entity' },
  ];
}

// ============================================================================
// Queue Summary Types for Validation
// ============================================================================

export interface QueueSummary {
  total_pending: number;
  pending_by_complexity: {
    simple: number;
    moderate: number;
    complex: number;
    very_complex: number;
  };
  average_wait_time_minutes?: number;
  oldest_pending_hours?: number;
  recent_approval_rate: number;
  queue_status: 'normal' | 'high' | 'critical';
}

// ============================================================================
// Chat Service (Knowledge Base Query)
// ============================================================================

export interface ChatSource {
  document_id: string;
  title: string;
  file_name: string;
  score: number;
  excerpt: string;
  source_type?: string;
  compliance_metadata?: ComplianceData;
}

export interface StreamChatCallbacks {
  onChunk: (text: string) => void;
  onSources: (sources: ChatSource[]) => void;
  onDone: () => void;
  onError?: (error: Error) => void;
  onContentUpdate?: (text: string) => void;
  onSessionId?: (sessionId: string, sessionTitle?: string) => void;
}

export const chatService = {
  /**
   * Send a streaming chat message to the Knowledge Base
   * Uses ERP endpoint: orgain.kb.api.chat_api.stream_chat_message
   * Uses SSE for real-time streaming responses
   *
   * @param message - The query message
   * @param searchStrategy - Search strategy: 'weighted_fusion' (default), 'vector_first', 'graph_first', 'parallel'
   * @param model - LLM model to use (default: 'gpt-4o-mini')
   * @param callbacks - Streaming callbacks
   */
  async streamChatMessage(
    message: string,
    callbacks: StreamChatCallbacks,
    searchStrategy: string = 'weighted_fusion',
    model: string = 'gpt-4o-mini',
    sessionId?: string | null,
    documentId?: string | null
  ): Promise<void> {
    const endpoint = getApiEndpoint('STREAM_CHAT_MESSAGE');
    if (!endpoint) {
      throw new Error('STREAM_CHAT_MESSAGE endpoint not configured');
    }

    try {
      // Use URL-encoded form data (Frappe whitelisted methods expect this format)
      const body = new URLSearchParams();
      body.append('message', message);
      body.append('model', model);
      body.append('search_strategy', searchStrategy);
      if (sessionId) {
        body.append('session_id', sessionId);
      }
      if (documentId) {
        // Pins tabular SQL queries to this Orgain Document. The backend
        // skips F7 multi-candidate picking and runs SQL against this
        // doc only. Omitted = legacy behaviour (LLM picks).
        body.append('document_id', documentId);
      }

      // Raw fetch bypasses the axios interceptor, so attach the CSRF token
      // manually. Frappe rejects POSTs without it (same reason as axios).
      const csrfToken = await getCsrfToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/x-www-form-urlencoded',
      };
      if (csrfToken) headers['X-Frappe-CSRF-Token'] = csrfToken;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        credentials: 'include', // Include cookies for ERPNext auth
        body: body.toString(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let doneCalled = false; // Prevent duplicate onDone calls

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // Handle non-SSE response (backend returned plain JSON instead of SSE)
          if (!doneCalled && buffer.trim()) {
            try {
              // Try to parse remaining buffer as plain JSON response
              const parsed = JSON.parse(buffer);
              const msg = parsed.message;
              // The backend short-circuits with a plain JSON envelope (no SSE
              // stream) when the chat can't run - most importantly when the
              // LLM/embedding API key isn't configured:
              //   { success: false, error: "llm_keys_missing", message: <text> }
              // Surface that reason via onError instead of silently ending the
              // stream with an empty reply.
              if (msg && msg.success === false) {
                doneCalled = true;
                const e = new Error(msg.message || msg.error || 'AI chat is unavailable.');
                (e as any).errorCode =
                  msg.error_code ||
                  (msg.error === 'llm_keys_missing' ? 'LLM_KEYS_MISSING' : undefined);
                callbacks.onError?.(e);
              } else if (msg?.content) {
                // Backend returned non-streaming format
                callbacks.onChunk(msg.content);
                if (msg.sources) {
                  callbacks.onSources(msg.sources);
                }
                if (msg.session_id && callbacks.onSessionId) {
                  callbacks.onSessionId(msg.session_id, msg.session_title);
                }
              }
            } catch {
              // Not valid JSON, ignore
            }
          }
          // Always call onDone when stream ends
          if (!doneCalled) {
            doneCalled = true;
            callbacks.onDone();
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();

            if (data === '[DONE]') {
              if (!doneCalled) {
                doneCalled = true;
                callbacks.onDone();
              }
              continue;
            }

            try {
              const parsed = JSON.parse(data);

              if (parsed.type === 'error' || parsed.success === false) {
                // Backend signalled a failure mid-stream (e.g. LLM keys
                // missing). Surface the clean reason and stop.
                doneCalled = true;
                const e = new Error(
                  parsed.message || parsed.error || 'AI chat is unavailable.',
                );
                (e as any).errorCode =
                  parsed.error_code ||
                  (parsed.error === 'llm_keys_missing' ? 'LLM_KEYS_MISSING' : undefined);
                callbacks.onError?.(e);
              } else if (parsed.type === 'content') {
                callbacks.onChunk(parsed.text);
              } else if (parsed.type === 'content_update') {
                // Handle citation re-numbering update from backend
                if (callbacks.onContentUpdate) {
                  callbacks.onContentUpdate(parsed.text);
                }
              } else if (parsed.type === 'sources') {
                callbacks.onSources(parsed.sources || []);
              } else if (parsed.type === 'session_id' || parsed.session_id) {
                // Handle session_id event or session_id in any event
                const sessionId = parsed.session_id || parsed.id;
                if (sessionId && callbacks.onSessionId) {
                  callbacks.onSessionId(sessionId, parsed.session_title);
                }
              } else if (parsed.type === 'done') {
                // Check for session_id in done event
                if (parsed.session_id && callbacks.onSessionId) {
                  callbacks.onSessionId(parsed.session_id, parsed.session_title);
                }
                if (!doneCalled) {
                  doneCalled = true;
                  callbacks.onDone();
                }
              }
            } catch (parseError) {
              logger.warn('Failed to parse SSE data:', data);
            }
          }
        }
      }
    } catch (error) {
      logger.error('KB chat stream error:', error);
      if (callbacks.onError) {
        callbacks.onError(error as Error);
      }
      throw error;
    }
  },

  /**
   * Send a non-streaming chat message (fallback).
   * Uses ERP endpoint: orgain.kb.api.chat_api.send_chat_message
   * Requires: Login
   *
   * @param message - The query message
   * @param searchStrategy - Search strategy: 'weighted_fusion' (default), 'vector_first', 'graph_first', 'parallel'
   * @param model - LLM model to use (default: 'gpt-4o-mini')
   */
  async sendChatMessage(
    message: string,
    searchStrategy: string = 'weighted_fusion',
    model: string = 'gpt-4o-mini',
    documentId?: string | null
  ): Promise<{
    message: string;
    sources: ChatSource[];
    model_used: string;
    created_at: string;
  }> {
    const endpoint = getApiEndpoint('SEND_CHAT_MESSAGE');
    if (!endpoint) {
      throw new Error('SEND_CHAT_MESSAGE endpoint not configured');
    }

    const body: Record<string, unknown> = {
      message,
      model,
      search_strategy: searchStrategy,
    };
    if (documentId) {
      body.document_id = documentId;
    }

    const response = await axios.post<{ message: {
      message: string;
      sources: ChatSource[];
      model_used: string;
      created_at: string;
    } }>(
      endpoint,
      body,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },
};

// ============================================================================
// Health Check
// ============================================================================

export interface HealthCheckResponse {
  status: string;
  version?: string;
  services?: {
    postgres?: boolean;
    neo4j?: boolean;
    qdrant?: boolean;
    redis?: boolean;
  };
  timestamp?: string;
}

export const healthService = {
  /**
   * Check health via ERPNext admin API.
   * Uses ERP endpoint: orgain.kb.api.admin_api.health_check
   */
  async checkHealth(): Promise<HealthCheckResponse> {
    const endpoint = getApiEndpoint('HEALTH_CHECK');
    if (!endpoint) {
      throw new Error('HEALTH_CHECK endpoint not configured');
    }

    const response = await axios.post<{ message: HealthCheckResponse }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data.message;
  },
};

// Entity Type Categories (for UI display)
export const ENTITY_TYPE_CATEGORIES = {
  core: { label: 'Core', color: 'blue', description: 'Fundamental entity types (Person, Organization, Location)' },
  operational: { label: 'Operational', color: 'green', description: 'Operational entities (Equipment, Service, Procedure)' },
  compliance: { label: 'Compliance', color: 'red', description: 'Regulatory and compliance entities (Safety, Standards)' },
  temporal: { label: 'Temporal', color: 'purple', description: 'Time-related entities (Date, Event)' },
  financial: { label: 'Financial', color: 'yellow', description: 'Financial entities (Money, Budget)' },
  natural: { label: 'Natural', color: 'lime', description: 'Natural world entities (Plant, Species)' },
  business: { label: 'Business', color: 'cyan', description: 'Business-related entities (Vendor, Customer)' },
  custom: { label: 'Custom', color: 'pink', description: 'User-defined custom entity types' },
};

export const getCategoryInfo = (category: string) => {
  return ENTITY_TYPE_CATEGORIES[category as keyof typeof ENTITY_TYPE_CATEGORIES] || {
    label: category,
    color: 'default',
    description: `${category} entity types`
  };
};

// ============================================================================
// Entity QA Types
// ============================================================================

export interface QADashboardSummary {
  total_entities: number;
  total_issues: number;
  auto_corrected: number;
  pending_review: number;
  resolved_count?: number;
  ignored_count?: number;
  quality_score: number;
  last_scan_at?: string;
  entity_breakdown: Record<string, number>;
  total_duplicate_clusters?: number;
  total_correction_rules?: number;
  total_stale_documents?: number;
}

export interface QAMetricDataPoint {
  date: string;
  quality_score: number;
  issues_found: number;
  auto_corrected: number;
  pending_review: number;
}

export type QAIssueSeverity = 'low' | 'medium' | 'high' | 'critical';
export type QAIssueType = 'duplicate' | 'inconsistent' | 'low_confidence' | 'missing_context' | 'invalid_format' | 'invalid_entry' | 'noise' | 'orphan' | 'invalid_relationship';
export type QAIssueStatus = 'pending' | 'resolved' | 'ignored';

export interface QAIssue {
  id: string;
  entity_id: string;
  entity_value: string;
  entity_type: string;
  issue_type: QAIssueType;
  severity: QAIssueSeverity;
  description: string;
  suggested_correction?: string;
  document_id: string;
  document_name: string;
  created_at: string;
  status: QAIssueStatus;
  resolved_at?: string;
  resolved_by?: string;
  resolution_notes?: string;
  resolution_action?: string;
}

export interface CanonicalEntity {
  id: string;
  canonical_value: string;
  entity_type: string;
  aliases: string[];
  occurrence_count: number;
  confidence_score: number;
  last_modified: string;
  created_at: string;
  notes?: string;
}

export interface CorrectionRule {
  id: string;
  name: string;
  pattern: string;
  correction: string;
  entity_type?: string;
  from_type?: string;
  to_type?: string;
  confidence?: number;
  action?: string;
  is_active: boolean;
  priority: number;
  times_applied: number;
  created_at?: string;
  updated_at?: string;
}

export interface RebuildGraphResponse {
  success: boolean;
  message: string;
  total_in_postgres?: number;
  created_in_neo4j?: number;
  deleted_from_neo4j?: number;
  errors?: number;
  duration_ms?: number;
}

export interface SmartFilterStats {
  total_candidates: number;
  skipped_high_confidence: number;
  skipped_pattern_match: number;
  skipped_majority_vote: number;
  sent_to_ai: number;
  cost_saved_usd: number;
}

export interface AIAnalysisResultItem {
  entity_id: string;
  entity_text: string;
  current_type: string;
  suggested_type: string;
  is_correct: boolean;
  confidence: 'high' | 'medium' | 'low';
  confidence_score: number;
  reasoning: string;
}

export interface AIAnalysisResponse {
  success: boolean;
  message?: string;
  total_analyzed: number;
  correct_classifications: number;
  suggested_corrections: number;
  high_confidence_corrections: number;
  estimated_cost_usd: number;
  total_tokens_used: number;
  processing_time_ms: number;
  results: AIAnalysisResultItem[];
  filter_stats?: SmartFilterStats;
  offset?: number;
  next_offset?: number | null;
  // Present only on the analyze-and-apply (Heal) endpoint.
  applied_count?: number;
  skipped_low_confidence?: number;
  apply_errors?: Array<{ entity_id: string; error: string }>;
}

// Entity Type Options - DEPRECATED: Use useEntityTypeOptions() hook instead.
// This static list is kept only as a fallback. The hook fetches from the API.
/** @deprecated Use useEntityTypeOptions() hook from '@/hooks/useEntityTypeOptions' */
export const ENTITY_TYPE_OPTIONS = [
  { value: 'PERSON', label: 'Person' },
  { value: 'ORGANIZATION', label: 'Organization' },
  { value: 'LOCATION', label: 'Location' },
  { value: 'DATE', label: 'Date' },
  { value: 'SAFETY', label: 'Safety' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'EQUIPMENT', label: 'Equipment' },
  { value: 'COMPLIANCE', label: 'Compliance' },
  { value: 'MONEY', label: 'Money' },
  { value: 'PRODUCT', label: 'Product' },
  { value: 'MEASUREMENT', label: 'Measurement' },
  { value: 'MATERIAL', label: 'Material' },
  { value: 'PLANT', label: 'Plant' },
  { value: 'SITE', label: 'Site' },
  { value: 'STANDARDS_BODY', label: 'Standards Body' },
  { value: 'CERTIFICATION', label: 'Certification' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'VENDOR', label: 'Vendor' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'CONTACT', label: 'Contact' },
  { value: 'CHEMICAL', label: 'Chemical' },
  { value: 'SCHEDULE', label: 'Schedule' },
  { value: 'PROCEDURE', label: 'Procedure' },
  { value: 'EVENT', label: 'Event' },
  { value: 'REGULATION', label: 'Regulation' },
  { value: 'GOVERNING_BODY', label: 'Governing Body' },
  { value: 'OTHER', label: 'Other' },
];

export interface ResolveIssueRequest {
  action: 'accept' | 'reject' | 'ignore' | 'remove' | 'merge';
  new_value?: string;
  corrected_type?: string;
  notes?: string;
}

export interface ResolveIssueResponse {
  success: boolean;
  issue_id: string;
  action: string;
  corrected_type?: string;
  message: string;
}

export interface BatchResolveRequest {
  issue_ids: string[];
  action: 'accept' | 'reject' | 'ignore';
  target_type?: string;
}

export interface BatchResolveResponse {
  success: boolean;
  processed: number;
  failed: number;
  results: string[];
  errors: string[];
}

export interface Neo4jRebuildStats {
  entities_created: number;
  entities_updated: number;
  entities_deleted: number;
  relationships_created: number;
  errors: number;
}

export interface Neo4jRebuildResponse {
  success: boolean;
  message: string;
  stats: Neo4jRebuildStats;
  duration_seconds: number;
}

export interface Neo4jStatsResponse {
  success: boolean;
  is_connected: boolean;
  total_nodes: number;
  total_relationships: number;
  entity_counts: Record<string, number>;
  last_sync_at?: string;
  error?: string;
}


// Vector Health Types
export interface VectorDocumentHealth {
  document_id: string;
  filename: string;
  expected_chunks: number;
  actual_vectors: number;
  coverage_percent: number;
  status: 'healthy' | 'warning' | 'critical';
  modified: string | null;
}

export interface CollectionStats {
  collection: string;
  vectors_count: number;
  points_count: number;
  indexed_vectors_count: number;
  status: string;
  error?: string;
}

export interface VectorHealthAudit {
  collection_stats: {
    staging: CollectionStats;
    production: CollectionStats;
  };
  total_documents: number;
  documents_healthy: number;
  documents_warning: number;
  documents_critical: number;
  orphaned_vector_documents: string[];
  document_health: VectorDocumentHealth[];
}

export interface StaleDocument {
  document_id: string;
  filename: string;
  reason: string;
  last_qa_action: string;
  last_embedding: string;
  chunk_count: number;
}

export interface StaleDocumentsResponse {
  stale_documents: StaleDocument[];
  total_stale: number;
}

export interface BatchReindexResponse {
  total: number;
  succeeded?: number;
  failed?: number;
  results?: Array<{ document_id: string; success: boolean; error?: string }>;
  async: boolean;
  message?: string;
}

export interface DuplicateClusterEntity {
  id: string;
  canonical_text: string;
  entity_type: string;
  confidence_score: number;
  occurrence_count: number;
  document_count: number;
}

export interface DuplicateCluster {
  cluster_id: string;
  normalized_text: string;
  entity_count: number;
  types_found: string[];
  entities: DuplicateClusterEntity[];
  total_occurrences: number;
  total_documents: number;
  is_cross_type: boolean;
}

export interface DuplicateClustersResponse {
  clusters: DuplicateCluster[];
  total_clusters: number;
  total_duplicate_entities: number;
}

export interface MergeClusterResponse {
  success: boolean;
  primary_id: string;
  merged_count: number;
  failed: { id: string; error: string }[];
  new_type: string | null;
}

export interface SelfLearningFieldSpec {
  value: string | number | boolean | null;
  type: 'bool' | 'int' | 'float' | 'str';
  min: number | null;
  max: number | null;
  description: string;
}

export interface SelfLearningSection {
  doctype: string;
  fields: Record<string, SelfLearningFieldSpec>;
  error?: string;
}

export interface SelfLearningSettingsResponse {
  sections: {
    qa_settings?: SelfLearningSection;
    brain_settings?: SelfLearningSection;
  };
}

export interface SelfLearningUpdateResponse {
  success: boolean;
  applied: { section: string; field: string; old: unknown; new: unknown }[];
  errors: { section: string; field?: string; error: string }[];
  applied_count: number;
  error_count: number;
}

// ============================================================================
// Entity QA Service
// ============================================================================

export const entityQAService = {
  /**
   * Get QA dashboard summary data.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.get_qa_dashboard
   */
  async getDashboard(): Promise<QADashboardSummary> {
    const endpoint = getApiEndpoint('GET_ENTITY_QA_DASHBOARD');
    if (!endpoint) {
      throw new Error('GET_ENTITY_QA_DASHBOARD endpoint not configured');
    }

    const response = await axios.get<{ message: QADashboardSummary }>(
      endpoint,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Get QA metrics for trend charts.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.get_qa_metrics
   */
  async getMetrics(days: number = 7): Promise<QAMetricDataPoint[]> {
    const endpoint = getApiEndpoint('GET_ENTITY_QA_METRICS');
    if (!endpoint) {
      throw new Error('GET_ENTITY_QA_METRICS endpoint not configured');
    }

    const response = await axios.get<{ message: QAMetricDataPoint[] }>(
      endpoint,
      {
        params: { days },
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message || [];
  },

  /**
   * Get pending QA issues for review.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.get_pending_issues
   */
  async getIssues(params?: {
    issue_type?: string;
    status?: string;
    severity?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ issues: QAIssue[]; total_count: number }> {
    const endpoint = getApiEndpoint('GET_ENTITY_QA_ISSUES');
    if (!endpoint) {
      throw new Error('GET_ENTITY_QA_ISSUES endpoint not configured');
    }

    const response = await axios.get<{ message: { issues: QAIssue[]; total_count: number } }>(
      endpoint,
      {
        params: {
          issue_type: params?.issue_type,
          status: params?.status,
          severity: params?.severity,
          search: params?.search,
          sort_by: params?.sort_by,
          sort_order: params?.sort_order,
          limit: params?.limit ?? 50,
          offset: params?.offset ?? 0,
        },
        withCredentials: true,
        // Search-path request: fail visibly rather than spin forever.
        timeout: 30000,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const data = response.data.message;
    if (data && typeof data === 'object' && 'issues' in data) {
      return { issues: data.issues || [], total_count: data.total_count || 0 };
    }
    // Backwards compatibility: if backend returns an array directly
    if (Array.isArray(data)) {
      return { issues: data, total_count: data.length };
    }
    return { issues: [], total_count: 0 };
  },

  /**
   * Resolve a QA issue.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.resolve_issue
   */
  async resolveIssue(issueId: string, request: ResolveIssueRequest): Promise<ResolveIssueResponse> {
    const endpoint = getApiEndpoint('RESOLVE_ENTITY_QA_ISSUE');
    if (!endpoint) {
      throw new Error('RESOLVE_ENTITY_QA_ISSUE endpoint not configured');
    }

    const response = await axios.post<{ message: ResolveIssueResponse }>(
      endpoint,
      {
        issue_id: issueId,
        ...request,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Batch resolve multiple issues.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.batch_resolve_issues
   */
  async batchResolveIssues(request: BatchResolveRequest): Promise<BatchResolveResponse> {
    const endpoint = getApiEndpoint('BATCH_RESOLVE_ENTITY_QA_ISSUES');
    if (!endpoint) {
      throw new Error('BATCH_RESOLVE_ENTITY_QA_ISSUES endpoint not configured');
    }

    const response = await axios.post<{ message: BatchResolveResponse }>(
      endpoint,
      {
        issue_ids: JSON.stringify(request.issue_ids),
        action: request.action,
        target_type: request.target_type,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Get canonical entities.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.get_canonical_entities
   */
  async getCanonicalEntities(params?: {
    entity_type?: string;
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<{ entities: CanonicalEntity[]; total_count: number }> {
    const endpoint = getApiEndpoint('GET_CANONICAL_ENTITIES');
    if (!endpoint) {
      throw new Error('GET_CANONICAL_ENTITIES endpoint not configured');
    }

    const response = await axios.get<{ message: { entities: CanonicalEntity[]; total_count: number } }>(
      endpoint,
      {
        params: {
          entity_type: params?.entity_type,
          search: params?.search,
          limit: params?.limit ?? 100,
          offset: params?.offset ?? 0,
          sort_by: params?.sort_by,
          sort_order: params?.sort_order,
        },
        withCredentials: true,
        // Search-path request: fail visibly rather than spin forever.
        timeout: 30000,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const data = response.data.message;
    if (data && typeof data === 'object' && 'entities' in data) {
      return { entities: data.entities || [], total_count: data.total_count || 0 };
    }
    // Backwards compatibility: if backend returns an array directly
    if (Array.isArray(data)) {
      return { entities: data, total_count: data.length };
    }
    return { entities: [], total_count: 0 };
  },

  /**
   * Get Neo4j graph statistics.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.get_neo4j_stats
   */
  async getNeo4jStats(): Promise<Neo4jStatsResponse> {
    const endpoint = getApiEndpoint('GET_ENTITY_QA_NEO4J_STATS');
    if (!endpoint) {
      throw new Error('GET_ENTITY_QA_NEO4J_STATS endpoint not configured');
    }

    const response = await axios.get<{ message: any }>(
      endpoint,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const data = response.data.message;
    return {
      success: data.success ?? false,
      is_connected: data.success ?? false,
      total_nodes: data.total_count ?? 0,
      total_relationships: data.total_occurrences ?? 0,
      entity_counts: data.by_type ?? {},
      last_sync_at: data.last_sync_at,
      error: data.error,
    };
  },

  /**
   * Rebuild Neo4j graph from PostgreSQL.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.rebuild_neo4j_graph
   */
  async rebuildNeo4jGraph(): Promise<RebuildGraphResponse> {
    const endpoint = getApiEndpoint('REBUILD_ENTITY_QA_NEO4J');
    if (!endpoint) {
      throw new Error('REBUILD_ENTITY_QA_NEO4J endpoint not configured');
    }

    const response = await axios.post<{ message: RebuildGraphResponse }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        timeout: 300000, // 5 minutes for rebuild
      }
    );

    return response.data.message;
  },

  /**
   * Get correction rules.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.get_correction_rules
   */
  async getCorrectionRules(): Promise<CorrectionRule[]> {
    const endpoint = getApiEndpoint('GET_ENTITY_QA_CORRECTION_RULES');
    if (!endpoint) {
      throw new Error('GET_ENTITY_QA_CORRECTION_RULES endpoint not configured');
    }

    const response = await axios.get<{ message: any }>(
      endpoint,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const data = response.data.message;
    // Backend returns {success: true, rules: [...]} or just an array
    if (data && typeof data === 'object' && 'rules' in data) {
      return data.rules || [];
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  },

  /**
   * Update a canonical entity.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.update_entity
   */
  async updateEntity(entityId: string, data: {
    entity_type?: string;
    confidence_score?: number;
    notes?: string;
  }): Promise<{ success: boolean; message: string }> {
    const endpoint = getApiEndpoint('UPDATE_CANONICAL_ENTITY');
    if (!endpoint) {
      throw new Error('UPDATE_CANONICAL_ENTITY endpoint not configured');
    }

    const response = await axios.post<{ message: { success: boolean; message: string } }>(
      endpoint,
      {
        entity_id: entityId,
        ...data,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Delete a canonical entity.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.delete_entity
   */
  async deleteEntity(entityId: string): Promise<{ success: boolean; message: string }> {
    const endpoint = getApiEndpoint('DELETE_CANONICAL_ENTITY');
    if (!endpoint) {
      throw new Error('DELETE_CANONICAL_ENTITY endpoint not configured');
    }

    const response = await axios.post<{ message: { success: boolean; message: string } }>(
      endpoint,
      { entity_id: entityId },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Bulk delete canonical entities.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.bulk_delete_entities
   */
  async bulkDeleteEntities(entityIds: string[]): Promise<{
    success: boolean;
    deleted: number;
    deleted_count?: number;
    failed: number;
    failed_count?: number;
    message: string;
  }> {
    const endpoint = getApiEndpoint('BULK_DELETE_CANONICAL_ENTITIES');
    if (!endpoint) {
      throw new Error('BULK_DELETE_CANONICAL_ENTITIES endpoint not configured');
    }

    const response = await axios.post<{ message: {
      success: boolean;
      deleted: number;
      deleted_count?: number;
      failed: number;
      failed_count?: number;
      message: string;
    } }>(
      endpoint,
      { entity_ids: JSON.stringify(entityIds) },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Bulk retype canonical entities to a new entity type.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.bulk_retype_entities
   */
  async bulkRetypeEntities(entityNames: string[], newEntityType: string): Promise<{
    success: boolean;
    updated_count: number;
    errors: any[];
  }> {
    const endpoint = getApiEndpoint('BULK_RETYPE_ENTITIES');
    if (!endpoint) {
      throw new Error('BULK_RETYPE_ENTITIES endpoint not configured');
    }

    const response = await axios.post<{ message: {
      success: boolean;
      updated_count: number;
      errors: any[];
    } }>(
      endpoint,
      {
        entity_names: JSON.stringify(entityNames),
        new_entity_type: newEntityType,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Run AI analysis on entities.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.ai_analyze_entities
   */
  async aiAnalyzeEntities(params: {
    confidenceThreshold?: number;
    limit?: number;
    offset?: number;
  }): Promise<AIAnalysisResponse> {
    const endpoint = getApiEndpoint('AI_ANALYZE_ENTITIES');
    if (!endpoint) {
      throw new Error('AI_ANALYZE_ENTITIES endpoint not configured');
    }

    const response = await axios.post<{ message: AIAnalysisResponse }>(
      endpoint,
      {
        confidence_threshold: params.confidenceThreshold ?? 0.7,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        timeout: 180000, // 3 minutes for AI analysis
      }
    );

    return response.data.message;
  },

  /**
   * Run AI analysis and auto-apply high-confidence corrections.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.ai_analyze_and_apply
   */
  async aiAnalyzeAndApply(params: {
    confidenceThreshold?: number;
    limit?: number;
    autoApplyThreshold?: number;
    offset?: number;
  }): Promise<AIAnalysisResponse> {
    const endpoint = getApiEndpoint('AI_ANALYZE_AND_APPLY');
    if (!endpoint) {
      throw new Error('AI_ANALYZE_AND_APPLY endpoint not configured');
    }

    const response = await axios.post<{ message: AIAnalysisResponse }>(
      endpoint,
      {
        confidence_threshold: params.confidenceThreshold ?? 0.7,
        limit: params.limit ?? 50,
        auto_apply_threshold: params.autoApplyThreshold ?? 0.9,
        offset: params.offset ?? 0,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        timeout: 180000, // 3 minutes for AI analysis
      }
    );

    return response.data.message;
  },

  /**
   * Merge two canonical entities.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.merge_entities
   */
  async mergeEntities(primaryId: string, secondaryId: string): Promise<{
    success: boolean;
    primary_id: string;
    secondary_id: string;
    message: string;
  }> {
    const endpoint = getApiEndpoint('MERGE_CANONICAL_ENTITIES');
    if (!endpoint) {
      throw new Error('MERGE_CANONICAL_ENTITIES endpoint not configured');
    }

    const response = await axios.post<{ message: {
      success: boolean;
      primary_id: string;
      secondary_id: string;
      message: string;
    } }>(
      endpoint,
      {
        primary_id: primaryId,
        secondary_id: secondaryId,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Add a new correction rule.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.add_correction_rule
   */
  async addCorrectionRule(rule: {
    name: string;
    from_type: string;
    to_type: string;
    pattern?: string;
    confidence?: number;
    action?: string;
    priority?: number;
  }): Promise<{
    success: boolean;
    rule_name: string;
    rule_id: string;
    message: string;
  }> {
    const endpoint = getApiEndpoint('ADD_CORRECTION_RULE');
    if (!endpoint) {
      throw new Error('ADD_CORRECTION_RULE endpoint not configured');
    }

    const response = await axios.post<{ message: {
      success: boolean;
      rule_name: string;
      rule_id: string;
      message: string;
    } }>(
      endpoint,
      {
        name: rule.name,
        from_type: rule.from_type,
        to_type: rule.to_type,
        pattern: rule.pattern,
        confidence: rule.confidence ?? 0.85,
        action: rule.action ?? 'review_queue',
        priority: rule.priority ?? 0,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Delete a correction rule.
   */
  /**
   * Update an existing correction rule.
   */
  async updateCorrectionRule(ruleId: string, rule: {
    name?: string;
    from_type?: string;
    to_type?: string;
    pattern?: string;
    confidence?: number;
    action?: string;
    priority?: number;
  }): Promise<{ success: boolean; rule_id: string; message: string }> {
    const endpoint = getApiEndpoint('UPDATE_CORRECTION_RULE');
    if (!endpoint) {
      throw new Error('UPDATE_CORRECTION_RULE endpoint not configured');
    }

    const response = await axios.post<{ message: { success: boolean; rule_id: string; message: string } }>(
      endpoint,
      {
        rule_id: ruleId,
        name: rule.name,
        from_type: rule.from_type,
        to_type: rule.to_type,
        pattern: rule.pattern,
        confidence: rule.confidence,
        action: rule.action,
        priority: rule.priority,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  async deleteCorrectionRule(ruleId: string): Promise<{ success: boolean; message: string }> {
    const endpoint = getApiEndpoint('DELETE_CORRECTION_RULE');
    if (!endpoint) {
      throw new Error('DELETE_CORRECTION_RULE endpoint not configured');
    }

    const response = await axios.post<{ message: { success: boolean; message: string } }>(
      endpoint,
      { rule_id: ruleId },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Toggle a correction rule active/inactive.
   */
  async toggleCorrectionRule(ruleId: string): Promise<{ success: boolean; is_active: boolean; message: string }> {
    const endpoint = getApiEndpoint('TOGGLE_CORRECTION_RULE');
    if (!endpoint) {
      throw new Error('TOGGLE_CORRECTION_RULE endpoint not configured');
    }

    const response = await axios.post<{ message: { success: boolean; is_active: boolean; message: string } }>(
      endpoint,
      { rule_id: ruleId },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  async reviewDataQuality(): Promise<{ success: boolean; data: { total_scanned: number; invalid_found: number; issues_created: number; already_reported: number; noise_found?: number; duplicates_found?: number; auto_merged?: number } }> {
    const endpoint = getApiEndpoint('REVIEW_DATA_QUALITY');
    if (!endpoint) {
      throw new Error('REVIEW_DATA_QUALITY endpoint not configured');
    }

    const response = await axios.post<{ message: { success: boolean; data: { total_scanned: number; invalid_found: number; issues_created: number; already_reported: number; noise_found?: number; duplicates_found?: number; auto_merged?: number } } }>(
      endpoint,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return response.data.message;
  },

  /**
   * Bulk dismiss multiple QA issues.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.bulk_dismiss_issues
   */
  async bulkDismissIssues(issueIds: string[], reason?: string): Promise<{ success: boolean; dismissed_count: number; failed_count: number }> {
    const endpoint = getApiEndpoint('BULK_DISMISS_ISSUES');
    if (!endpoint) {
      throw new Error('BULK_DISMISS_ISSUES endpoint not configured');
    }

    const response = await axios.post<{ message: { success: boolean; dismissed_count: number; failed_count: number } }>(
      endpoint,
      {
        issue_ids: JSON.stringify(issueIds),
        reason: reason || '',
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Bulk resolve multiple QA issues.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.bulk_resolve_issues
   */
  async bulkResolveIssues(issueIds: string[], action?: string): Promise<{ success: boolean; resolved_count: number; failed_count: number }> {
    const endpoint = getApiEndpoint('BULK_RESOLVE_ISSUES');
    if (!endpoint) {
      throw new Error('BULK_RESOLVE_ISSUES endpoint not configured');
    }

    const response = await axios.post<{ message: { success: boolean; resolved_count: number; failed_count: number } }>(
      endpoint,
      {
        issue_ids: JSON.stringify(issueIds),
        action: action || 'accept',
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Delete QA issues by their IDs.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.delete_qa_issues
   */
  async deleteQaIssues(qaIds: string[]): Promise<{ success: boolean; deleted: number; deleted_count?: number; failed: any[]; failed_count?: number; total: number; message: string }> {
    const endpoint = getApiEndpoint('DELETE_QA_ISSUES');
    if (!endpoint) {
      throw new Error('DELETE_QA_ISSUES endpoint not configured');
    }

    const response = await axios.post<{ message: { success: boolean; deleted: number; deleted_count?: number; failed: any[]; failed_count?: number; total: number; message: string } }>(
      endpoint,
      {
        qa_ids: JSON.stringify(qaIds),
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Get breakdown of issues by type.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.get_issue_type_breakdown
   */
  async getIssueTypeBreakdown(): Promise<{ breakdown: Record<string, number>; total: number }> {
    const endpoint = getApiEndpoint('GET_ISSUE_TYPE_BREAKDOWN');
    if (!endpoint) {
      throw new Error('GET_ISSUE_TYPE_BREAKDOWN endpoint not configured');
    }

    const response = await axios.get<{ message: { breakdown: Record<string, number>; total: number } }>(
      endpoint,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data.message;
  },

  /**
   * Auto-fix pending QA issues (noise delete, duplicate merge, orphan dismiss).
   */
  async autoFixIssues(dryRun: boolean = true): Promise<{
    success: boolean;
    dry_run: boolean;
    noise_deleted: number;
    duplicates_merged: number;
    orphans_dismissed: number;
    total_fixed: number;
    errors: string[] | null;
    message: string;
  }> {
    const endpoint = getApiEndpoint('AUTO_FIX_ISSUES');
    if (!endpoint) {
      throw new Error('AUTO_FIX_ISSUES endpoint not configured');
    }

    const response = await axios.post(
      endpoint,
      { dry_run: dryRun ? 'true' : 'false' },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return response.data.message;
  },

  /**
   * Auto-fix all issues of a specific type.
   */
  async autoFixByType(issueType: string, action: string): Promise<{
    success: boolean;
    issue_type: string;
    action: string;
    fixed: number;
    failed: number;
    message: string;
  }> {
    const endpoint = getApiEndpoint('AUTO_FIX_BY_TYPE');
    if (!endpoint) {
      throw new Error('AUTO_FIX_BY_TYPE endpoint not configured');
    }

    const response = await axios.post(
      endpoint,
      { issue_type: issueType, action },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return response.data.message;
  },

  /**
   * Resolve a duplicate issue by merging entities.
   */
  async resolveDuplicateIssue(issueId: string, primaryId: string, secondaryId: string): Promise<{
    success: boolean;
    merge_result: any;
    message: string;
  }> {
    const endpoint = getApiEndpoint('RESOLVE_DUPLICATE_ISSUE');
    if (!endpoint) {
      throw new Error('RESOLVE_DUPLICATE_ISSUE endpoint not configured');
    }

    const response = await axios.post(
      endpoint,
      { issue_id: issueId, primary_id: primaryId, secondary_id: secondaryId },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return response.data.message;
  },

  /**
   * Audit vector health across all documents.
   */
  async auditVectorHealth(limit: number = 100): Promise<VectorHealthAudit> {
    const endpoint = getApiEndpoint('AUDIT_VECTOR_HEALTH');
    if (!endpoint) {
      throw new Error('AUDIT_VECTOR_HEALTH endpoint not configured');
    }

    const response = await axios.get<{ message: VectorHealthAudit }>(
      endpoint,
      {
        params: { limit },
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return response.data.message;
  },

  /**
   * Get documents with stale vectors due to QA actions.
   */
  async getStaleDocuments(): Promise<StaleDocumentsResponse> {
    const endpoint = getApiEndpoint('GET_STALE_DOCUMENTS');
    if (!endpoint) {
      throw new Error('GET_STALE_DOCUMENTS endpoint not configured');
    }

    const response = await axios.get<{ message: StaleDocumentsResponse }>(
      endpoint,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return response.data.message;
  },

  /**
   * Batch re-index documents to refresh their vector embeddings.
   */
  async batchReindexDocuments(documentIds: string[]): Promise<BatchReindexResponse> {
    const endpoint = getApiEndpoint('BATCH_REINDEX_DOCUMENTS');
    if (!endpoint) {
      throw new Error('BATCH_REINDEX_DOCUMENTS endpoint not configured');
    }

    const response = await axios.post<{ message: BatchReindexResponse }>(
      endpoint,
      { document_ids: JSON.stringify(documentIds) },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return response.data.message;
  },

  async getDuplicateClusters(minClusterSize?: number): Promise<DuplicateClustersResponse> {
    const endpoint = getApiEndpoint('GET_DUPLICATE_CLUSTERS');
    if (!endpoint) throw new Error('GET_DUPLICATE_CLUSTERS endpoint not configured');
    const response = await axios.get(endpoint, {
      params: {
        min_cluster_size: minClusterSize || 2,
        include_cross_type: 'true',
      },
      withCredentials: true,
    });
    const data = response.data?.message || response.data;
    return data || { clusters: [], total_clusters: 0, total_duplicate_entities: 0 };
  },

  async mergeCluster(
    entityIds: string[],
    primaryId?: string,
    targetType?: string
  ): Promise<MergeClusterResponse> {
    const endpoint = getApiEndpoint('MERGE_CLUSTER');
    if (!endpoint) throw new Error('MERGE_CLUSTER endpoint not configured');
    const response = await axios.post(
      endpoint,
      {
        entity_ids: JSON.stringify(entityIds),
        primary_id: primaryId,
        target_type: targetType,
      },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return response.data?.message || response.data;
  },

  /**
   * Read self-learning settings (QA majority-vote + Brain Settings reviewer/classifier).
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.get_self_learning_settings
   */
  async getSelfLearningSettings(): Promise<SelfLearningSettingsResponse> {
    const endpoint = getApiEndpoint('GET_SELF_LEARNING_SETTINGS');
    if (!endpoint) throw new Error('GET_SELF_LEARNING_SETTINGS endpoint not configured');
    const response = await axios.get<{ message: SelfLearningSettingsResponse }>(
      endpoint,
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
    );
    return response.data.message;
  },

  /**
   * Update self-learning settings.
   * Uses ERP endpoint: orgain.kb.api.entity_qa_api.update_self_learning_settings
   */
  async updateSelfLearningSettings(
    payload: { qa_settings?: Record<string, unknown>; brain_settings?: Record<string, unknown> }
  ): Promise<SelfLearningUpdateResponse> {
    const endpoint = getApiEndpoint('UPDATE_SELF_LEARNING_SETTINGS');
    if (!endpoint) throw new Error('UPDATE_SELF_LEARNING_SETTINGS endpoint not configured');
    const response = await axios.post<{ message: SelfLearningUpdateResponse }>(
      endpoint,
      { payload: JSON.stringify(payload) },
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
    );
    return response.data.message;
  },

  async getValidEntityTypes(): Promise<string[]> {
    const endpoint = getApiEndpoint('GET_VALID_ENTITY_TYPES');
    if (!endpoint) {
      // Fallback hardcoded list matching backend VALID_ENTITY_TYPES
      return [
        'ADDRESS', 'CARDINAL', 'CERTIFICATION', 'CHEMICAL', 'COMPLIANCE',
        'CONTACT', 'CUSTOMER', 'DATE', 'DURATION', 'EMAIL', 'EQUIPMENT',
        'EVENT', 'FAC', 'FREQUENCY', 'GPE', 'LANGUAGE', 'LAW', 'LOCATION',
        'MATERIAL', 'MEASUREMENT', 'MONEY', 'NORP', 'ORDINAL', 'ORGANIZATION',
        'PERCENT', 'PERSON', 'PHONE', 'PLANT', 'PRODUCT', 'QUANTITY',
        'REGULATION', 'SAFETY', 'SCHEDULE', 'SERVICE', 'SITE', 'TIME',
        'URL', 'WORK_OF_ART',
      ];
    }
    const response = await axios.get(endpoint, { withCredentials: true });
    return response.data?.message || response.data || [];
  },
};

// Utility functions for Entity QA
export function getQASeverityColor(severity: QAIssueSeverity): string {
  switch (severity) {
    case 'critical': return '#ef4444'; // red
    case 'high': return '#f97316'; // orange
    case 'medium': return '#eab308'; // yellow
    case 'low': return '#22c55e'; // green
    default: return '#6b7280'; // gray
  }
}

export function getQAIssueTypeLabel(type: QAIssueType): string {
  switch (type) {
    case 'duplicate': return 'Duplicate';
    case 'inconsistent': return 'Misclassified';
    case 'low_confidence': return 'Low Confidence';
    case 'missing_context': return 'Missing Context';
    case 'invalid_format': return 'Invalid Format';
    case 'invalid_entry': return 'Invalid Entry';
    case 'noise': return 'Noise / Garbage';
    case 'orphan': return 'Orphaned Entity';
    case 'invalid_relationship': return 'Invalid Relationship';
    default: return type;
  }
}
